export type TopicGuideSection = {
  id: string;
  title: string;
  body: readonly string[];
  checklist?: readonly string[];
  sourceIds: readonly string[];
};

export type TopicGuideFaq = {
  id: string;
  question: string;
  answer: string;
  sourceIds: readonly string[];
};

export type TopicGuide = {
  topicId: string;
  title?: string;
  locale: "ko";
  publicationStatus:
    | "korean_test_publish"
    | "korean_test_preview"
    | "review_required"
    | "verified_publish";
  risk: "R1" | "R2" | "R3" | "R4";
  checkedAt: string;
  jurisdictionLabel: string;
  summary: string;
  keyPoints: readonly string[];
  sections: readonly TopicGuideSection[];
  faqs: readonly TopicGuideFaq[];
  sourceIds: readonly string[];
  additionalChecks: readonly string[];
  disclaimer: string;
};

export const TOPIC_GUIDES = [
  {
    topicId: "school",
    title: "시드니 공립학교 등록 서류가 부족할 때: 학군 확인부터 주소 증명까지",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-09-15",
    jurisdictionLabel: "NSW 공립학교 Kindergarten-Year 12",
    summary:
      "NSW 공립학교 등록은 먼저 실제 거주 주소의 local school을 찾고 학교에 필요한 서류를 확인하는 순서로 진행합니다. 주소 증명이 부족하거나 임시비자·이사 직후·추가 지원이 필요한 상황이라면 신청을 포기하지 말고 학교에 현재 사정을 설명해 가능한 증빙과 별도 절차를 확인하세요.",
    keyPoints: [
      "School Finder에 실제 거주 주소를 입력해 local school을 먼저 확인하세요.",
      "가장 가까운 학교와 지정 local school이 항상 같지는 않습니다.",
      "신원, 주소, 예방접종 기록과 필요한 법원·건강·학습지원 서류를 준비하세요.",
      "일부 학교는 local enrolment에 100-point 주소 확인을 사용할 수 있습니다.",
      "주소 서류가 부족한 새 이주자·주거불안 가정 등은 학교에 사정을 설명하고 대체 확인을 문의할 수 있습니다.",
      "Non-local 신청은 자리가 있다는 보장이 없으므로 기존 학교 등록을 먼저 취소하지 마세요.",
    ],
    sections: [
      {
        id: "find-local-school",
        title: "1. 실제 거주 주소로 local school을 확인하세요",
        body: [
          "NSW School Finder에서 자녀가 실제로 사는 전체 주소를 입력하고 primary 또는 high school을 선택하세요. 지도에 표시되는 지정 enrolment area와 학교 연락처를 저장합니다. 단순히 집에서 가장 가까워 보이는 학교가 지정 local school이라고 가정하지 마세요.",
          "School Finder 정보는 바뀔 수 있으므로 이사 예정 주소나 경계 부근 주소라면 신청 전에 표시된 학교에 전화나 이메일로 주소와 학년을 알려 local enrolment 여부를 다시 확인하세요.",
        ],
        checklist: [
          "자녀가 실제로 거주하는 전체 주소",
          "입학을 원하는 학년과 시작 시기",
          "School Finder 결과 화면 또는 학교 연락처",
          "이사 예정이면 계약·settlement 시기",
        ],
        sourceIds: ["school-source-finder", "school-source-enrolment"],
      },
      {
        id: "contact-school",
        title: "2. 학교에 신청 방식과 자리를 확인하세요",
        body: [
          "학교 웹사이트의 enrolment 안내를 확인한 뒤 온라인 또는 종이 신청 중 이용 가능한 방식을 학교에 문의하세요. 온라인 신청은 모든 학생 상황에 적용되는 것이 아니므로 링크가 보이지 않거나 신청이 진행되지 않으면 학교에 직접 연락합니다.",
          "지정 지역 밖의 학교를 원하면 non-local 신청으로 처리될 수 있습니다. Non-local 신청은 학교의 현재 수용 가능 인원과 공개된 기준에 따라 검토되므로, 희망 학교의 결과가 확정되기 전에 현재 학교 등록을 먼저 취소하지 않는 것이 안전합니다.",
        ],
        sourceIds: [
          "school-source-enrolment",
          "school-source-procedures",
          "school-source-2026-update",
        ],
      },
      {
        id: "prepare-documents",
        title: "3. 신원·주소·건강·지원 서류를 한 묶음으로 준비하세요",
        body: [
          "새 공립학교 등록에는 자녀의 출생증명서 또는 신원서류, 거주 주소 증명, Australian Immunisation Register의 예방접종 기록, 해당하는 가족법·법원 명령이 요청될 수 있습니다. 건강·장애·학습지원이 필요하면 의료·응급계획과 기존 지원계획도 학교에 알립니다.",
          "학교가 enrolment buffer에 가깝거나 이를 넘은 경우 local enrolment 확인에 100-point residential address check를 사용할 수 있습니다. 부동산 중개인을 통한 6개월 이상 lease 또는 bond 영수증, rates notice, 공과금, 정부 발급 신분증 등 공식표의 자료를 자신의 상황에 맞게 조합하되 학교가 요구하는 최신성·이름·주소 일치를 확인하세요.",
        ],
        checklist: [
          "자녀 birth certificate, passport 등 신원·생년월일 자료",
          "부모·보호자 이름과 실제 주소가 표시된 lease, rates 또는 공과금",
          "Australian Immunisation Register history statement",
          "해당하는 가족법·법원 명령",
          "의료·응급 action plan과 학습·장애 지원 자료",
          "임시거주자라면 여권, 현재·이전 비자와 학교가 요청한 추가 문서",
        ],
        sourceIds: ["school-source-primary", "school-source-procedures"],
      },
      {
        id: "missing-address-evidence",
        title: "4. 주소 증명이 부족하면 사정을 서면으로 설명하세요",
        body: [
          "비공식 동거, 막 이사한 경우, 영구 주소가 없는 경우처럼 일반 lease나 bill이 없다면 허위 주소나 다른 사람의 주소를 사용하지 마세요. 학교에 실제 거주 상황과 현재 보유한 자료를 설명하고 statutory declaration 등 학교가 받아들일 수 있는 대체 증빙을 확인합니다.",
          "NSW 절차는 새로 호주에 도착한 가정, refugee background, homelessness, trauma, Defence relocation 등 사정이 있을 때 principal이 100-point 확인 요건을 면제하거나 조정할 수 있도록 합니다. 자동 면제를 단정할 수는 없으므로 해당 사정을 학교에 조기에 알리고 필요한 지원과 개인정보 제출 방법을 문의하세요.",
        ],
        sourceIds: ["school-source-procedures", "school-source-2026-update"],
      },
      {
        id: "submit-and-follow-up",
        title: "5. 영어 신청서를 제출하고 결과를 기록하세요",
        body: [
          "NSW Government school 신청서는 영어로 작성해야 합니다. 번역본은 내용을 이해하고 영어 신청서를 작성하는 데 참고할 수 있습니다. 제출 전 이름, 주소, 연락처, 비자·건강·지원 정보가 증빙과 일치하는지 확인하고 제출본과 학교 연락 기록을 보관하세요.",
          "학교가 추가 자료를 요청하면 요청 항목과 기한을 서면으로 확인합니다. Non-local 결과나 지원 배치가 필요한 경우 일반 enrolment와 결정 과정이 다를 수 있으므로, 결과 통지를 받은 뒤 다음 단계를 학교에 확인하세요. 통역이 필요하면 NSW 안내에 연결된 Telephone Interpreter Service 이용 방법도 확인할 수 있습니다.",
        ],
        sourceIds: [
          "school-source-enrolment",
          "school-source-primary",
          "school-source-2026-update",
        ],
      },
    ],
    faqs: [
      {
        id: "closest-school",
        question: "집에서 가장 가까운 공립학교가 자동으로 local school인가요?",
        answer:
          "항상 그렇지는 않습니다. NSW Department of Education이 정한 enrolment area는 거리만으로 결정되지 않을 수 있으므로 School Finder에 실제 주소를 입력하고 학교에 다시 확인하세요.",
        sourceIds: ["school-source-finder", "school-source-enrolment"],
      },
      {
        id: "no-lease",
        question: "제 이름으로 된 lease나 공과금이 없으면 등록할 수 없나요?",
        answer:
          "그 사실만으로 불가능하다고 단정할 수 없습니다. 실제 거주 상황을 학교에 설명하고 현재 가진 자료와 statutory declaration 등 가능한 대체 확인 방법을 문의하세요. 허위 주소나 서류를 제출하면 안 됩니다.",
        sourceIds: ["school-source-procedures"],
      },
      {
        id: "hundred-points",
        question: "모든 학교가 반드시 100-point 주소 확인을 하나요?",
        answer:
          "아닙니다. 공식 절차는 enrolment buffer에 가깝거나 이를 넘은 학교의 local enrolment 확인에 이 방식을 사용하도록 안내합니다. 실제 요구 자료는 신청 학교에 확인하세요.",
        sourceIds: ["school-source-procedures", "school-source-primary"],
      },
      {
        id: "temporary-resident",
        question: "임시비자 자녀도 local public school에 신청할 수 있나요?",
        answer:
          "NSW의 2026 안내는 temporary resident visa 학생도 거주 주소에 따라 local public school에 등록할 수 있다고 설명합니다. 다만 비자·Authority to Enrol·비용 등 개인별 절차는 학교와 Temporary Residents Program에서 현재 조건을 확인하세요.",
        sourceIds: ["school-source-primary", "school-source-2026-update"],
      },
      {
        id: "non-local",
        question: "학군 밖 학교에도 신청할 수 있나요?",
        answer:
          "신청은 가능할 수 있지만 학교의 수용 가능 인원과 공개된 non-local 기준에 따라 검토됩니다. 희망 학교에 자리가 있는지와 신청 기준을 먼저 확인하세요.",
        sourceIds: ["school-source-enrolment", "school-source-procedures"],
      },
      {
        id: "changing-school",
        question: "다른 학교 결과를 기다리는 동안 기존 학교를 먼저 그만둬야 하나요?",
        answer:
          "먼저 취소하지 않는 것이 안전합니다. 새 학교의 신청 결과와 수락 절차를 확인한 뒤 기존 학교에 전학 처리를 문의하세요.",
        sourceIds: ["school-source-primary", "school-source-2026-update"],
      },
      {
        id: "support-needs",
        question: "건강·장애·학습지원이 필요하면 언제 알려야 하나요?",
        answer:
          "신청 준비 단계에서 local school principal에게 알리고 의료·응급계획과 기존 학습지원 자료를 제공하세요. 지원 class나 specialist setting은 별도 절차가 있을 수 있습니다.",
        sourceIds: ["school-source-primary", "school-source-finder"],
      },
      {
        id: "korean-form",
        question: "한국어 번역 신청서만 제출해도 되나요?",
        answer:
          "아닙니다. 공식 안내상 신청서는 영어로 작성해야 합니다. 번역본은 영어 신청서를 이해하고 작성하는 참고자료로 사용하세요.",
        sourceIds: ["school-source-enrolment", "school-source-primary"],
      },
    ],
    sourceIds: [
      "school-source-finder",
      "school-source-enrolment",
      "school-source-primary",
      "school-source-procedures",
      "school-source-2026-update",
    ],
    additionalChecks: [
      "신청 직전 School Finder의 현재 enrolment area와 학교 연락처를 다시 확인하세요.",
      "학교가 요구하는 주소 자료의 이름, 주소, 발급일과 point 조합을 확인하세요.",
      "Temporary resident, non-local, 지원 class 또는 specialist setting은 해당 학교의 별도 절차를 확인하세요.",
      "등록 결과가 확정되기 전에는 현재 학교 등록을 먼저 취소하지 마세요.",
    ],
    disclaimer:
      "이 안내는 NSW 공립학교 등록 절차에 관한 일반 생활정보이며 특정 자녀의 입학 자격, 학교 배정, non-local 승인, 비자 관련 비용 또는 지원 배치 결과를 결정하지 않습니다. 실제 거주지, 학생 신분, 학년, 학교 수용 가능 인원과 지원 필요에 따라 절차가 달라질 수 있으므로 신청 전 NSW Department of Education과 해당 학교의 최신 안내를 확인하세요.",
  },
  {
    topicId: "abn",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-15",
    jurisdictionLabel: "호주 전역",
    summary:
      "ABN은 사업체나 단체를 정부와 지역사회에서 식별하는 11자리 번호입니다. 누구나 받을 수 있는 번호는 아니므로, 먼저 실제로 사업을 운영하고 있거나 시작 단계에 있는지 확인한 뒤 공식 ABR 신청 페이지에서 무료로 신청하세요.",
    keyPoints: [
      "ABN과 TFN은 서로 다른 번호입니다.",
      "직원으로 하는 업무에는 ABN 자격이 생기지 않습니다.",
      "사업을 아직 시작하지 않았다면 준비 활동과 그 증빙이 필요할 수 있습니다.",
      "신청 전 사업 형태, 신원 정보, 사업 활동과 연락처를 준비하면 편리합니다.",
      "발급 후 등록 정보가 바뀌면 28일 안에 업데이트해야 합니다.",
    ],
    sections: [
      {
        id: "check-entitlement",
        title: "1. 먼저 ABN 신청 대상인지 확인하세요",
        body: [
          "일반적으로 호주에서 사업 또는 그 밖의 enterprise를 운영하거나 시작하는 경우, 호주의 간접세 구역과 연결된 공급을 하는 경우, 또는 Corporations Act에 따른 회사인 경우 ABN을 신청할 수 있습니다.",
          "단순히 직원으로 일하는 것은 ABN 신청 자격이 되지 않습니다. 고용주가 직원 채용 조건으로 ABN 발급을 요구해서도 안 된다고 ABR은 안내합니다. 계약자처럼 불리더라도 실제 관계가 고용이라면 먼저 employee와 contractor 구분을 확인하세요.",
        ],
        sourceIds: ["abn-source-entitlement"],
      },
      {
        id: "prepare-business",
        title: "2. 사업 시작 사실과 기본 정보를 준비하세요",
        body: [
          "사업이 아직 본격적으로 시작되지 않았다면 웹사이트나 소셜 계정 개설, 장비·재고 구입, 보험·허가 준비, 견적 발행, 사업장 임차처럼 실제로 시작을 준비한 활동이 있어야 하며 나중에 증빙을 요청받을 수 있습니다.",
          "신청 전에 sole trader, partnership, company 등 사업 형태를 정하고, 상황에 따라 TFN과 관계자 정보, 법적 명칭, 사업 활동, 사업장 주소, 이메일과 전화번호 등을 준비하세요.",
        ],
        checklist: [
          "사업 형태와 법적 명칭",
          "본인 및 필요한 관계자의 TFN·신원 정보",
          "사업 시작 예정일과 주된 사업 활동",
          "사업장·우편 주소, 이메일, 전화번호",
          "회사라면 ACN 또는 ARBN 등 해당 정보",
        ],
        sourceIds: ["abn-source-entitlement", "abn-source-preparation"],
      },
      {
        id: "apply-officially",
        title: "3. 공식 ABR 페이지에서 신청하세요",
        body: [
          "ABN 신청은 무료입니다. 비공식 대행 사이트의 유료 서비스와 혼동하지 말고 Australian Business Register의 공식 신청 링크를 이용하세요. 모든 정보를 한 번에 준비하지 못했다면 신청을 저장할 수 있지만 ABN 발급이 늦어질 수 있습니다.",
          "신청이 성공하면 보통 11자리 ABN을 즉시 받고, 확인 화면과 등록 내용을 저장할 수 있습니다. ABN은 TFN을 대신하지 않으며, GST 같은 다른 사업 등록은 별도로 확인해야 합니다.",
        ],
        sourceIds: ["abn-source-applying", "abn-source-business-guide"],
      },
      {
        id: "understand-outcome",
        title: "4. 신청 결과를 확인하세요",
        body: [
          "ABN 대신 reference number를 받았다면 신청 정보 확인이나 추가 자료가 필요할 수 있습니다. ABR은 이런 신청을 20영업일 안에 검토하는 것을 목표로 하며, 필요한 경우 신청자에게 연락합니다.",
          "신청이 거절되면 refusal number와 함께 거절 이유 및 재검토 권리를 안내하는 편지를 받게 됩니다. 처리 여부는 ABN Lookup에서도 확인할 수 있습니다.",
        ],
        sourceIds: ["abn-source-applying", "abn-source-lookup"],
      },
      {
        id: "maintain-details",
        title: "5. 발급 후 정보를 최신으로 유지하세요",
        body: [
          "연락처, 주소, 사업 활동 등 ABR 등록 정보가 바뀌면 28일 안에 업데이트하세요. 사업을 중단하거나 사업 구조가 달라진 경우에는 기존 ABN을 계속 쓰는 것이 맞는지 확인하고, 필요하면 취소 또는 새 ABN 신청 절차를 진행해야 합니다.",
          "ABN Lookup에서는 ABN 상태, 사업 형태, GST 등록 상태 등 공개 정보를 확인할 수 있습니다. 고객이나 거래처에 전달하기 전 자신의 공개 정보가 맞는지 확인해 두면 좋습니다.",
        ],
        sourceIds: [
          "abn-source-maintenance",
          "abn-source-business-guide",
          "abn-source-lookup",
        ],
      },
    ],
    faqs: [
      {
        id: "abn-vs-tfn",
        question: "ABN과 TFN은 같은 번호인가요?",
        answer:
          "아닙니다. ABN은 사업체나 단체를 식별하는 11자리 번호이고, TFN을 대신하지 않습니다.",
        sourceIds: ["abn-source-applying", "abn-source-business-guide"],
      },
      {
        id: "abn-cost",
        question: "ABN 신청은 유료인가요?",
        answer:
          "호주 정부의 공식 등록 절차를 이용하면 무료입니다. 별도의 유료 대행 서비스를 이용할지는 본인이 선택할 수 있습니다.",
        sourceIds: ["abn-source-business-guide"],
      },
      {
        id: "employee-abn",
        question: "회사에서 ABN을 받아 오라고 하면 어떻게 하나요?",
        answer:
          "직원으로 수행하는 업무에는 ABN 자격이 생기지 않습니다. 명칭만 contractor라고 해서 자동으로 계약자가 되는 것도 아니므로 실제 근무 관계를 먼저 확인하세요.",
        sourceIds: ["abn-source-entitlement"],
      },
      {
        id: "before-opening",
        question: "영업을 시작하기 전에도 신청할 수 있나요?",
        answer:
          "가능할 수 있지만 사업을 실제로 시작하기 위한 준비 활동이 있어야 합니다. ABR이 시작 활동의 증빙을 요청할 수 있으므로 관련 기록을 보관하세요.",
        sourceIds: ["abn-source-entitlement"],
      },
      {
        id: "reference-number",
        question: "ABN 대신 reference number를 받았습니다.",
        answer:
          "신청 정보를 추가로 확인하거나 자료가 더 필요하다는 뜻일 수 있습니다. ABR은 20영업일 안에 검토하는 것을 목표로 하며 필요한 경우 연락합니다.",
        sourceIds: ["abn-source-applying"],
      },
      {
        id: "gst-separate",
        question: "ABN을 받으면 GST도 자동으로 등록되나요?",
        answer:
          "아닙니다. ABN 발급 후 GST를 포함한 다른 사업 등록을 별도로 신청할 수 있습니다. 자신의 GST 등록 의무는 공식 안내나 등록 세무사에게 확인하세요.",
        sourceIds: ["abn-source-applying"],
      },
      {
        id: "update-details",
        question: "주소나 사업 정보가 바뀌면 어떻게 하나요?",
        answer:
          "ABR 등록 정보가 바뀌면 28일 안에 업데이트해야 합니다. 사업 구조가 바뀌었다면 새 ABN이 필요한지도 함께 확인하세요.",
        sourceIds: ["abn-source-maintenance", "abn-source-business-guide"],
      },
    ],
    sourceIds: [
      "abn-source-entitlement",
      "abn-source-applying",
      "abn-source-preparation",
      "abn-source-business-guide",
      "abn-source-maintenance",
      "abn-source-lookup",
    ],
    additionalChecks: [
      "신청 전 현재 ABR 자격 조건과 준비 서류를 다시 확인하세요.",
      "사업 형태나 실제 근무 관계가 불분명하면 등록 세무사 또는 관련 전문가에게 확인하세요.",
    ],
    disclaimer:
      "이 안내는 일반적인 생활정보이며 개인의 세무·법률 자문이 아닙니다. 사업 형태와 실제 근무 관계에 따라 결과가 달라질 수 있으므로 중요한 결정은 ABR·ATO 공식 안내 또는 등록 세무사에게 확인하세요.",
  },
  {
    topicId: "tfn",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "호주 전역",
    summary:
      "TFN(Tax File Number)은 호주 세금과 연금 시스템에서 개인을 식별하는 번호로, 보통 9자리이며 한 번 발급받으면 평생 유지됩니다. 신청은 무료이고 체류 자격과 현재 위치에 따라 신청 방법이 다르므로, 자신의 상황에 맞는 ATO 공식 경로를 선택해 신청하세요.",
    keyPoints: [
      "TFN 신청은 무료이며, 유료 대행을 이용할 필요는 없습니다.",
      "TFN은 개인 번호이고 ABN은 사업체·단체 번호이므로 서로 다릅니다.",
      "외국 여권 소지 영주권자·임시비자 방문자는 호주에 있고 유효한 취업 권한이 있을 때 전용 온라인 경로를 이용할 수 있습니다.",
      "이미 TFN을 받은 적이 있다면 새로 신청하지 말고 기존 번호를 찾으세요.",
      "TFN은 현재 고용주, 은행, 등록 세무사처럼 정당한 필요가 있는 곳에만 제공하세요.",
      "취업을 시작했다면 고용주에게 TFN declaration을 제출해 원천징수 정보를 알리세요.",
    ],
    sections: [
      {
        id: "understand-tfn",
        title: "1. TFN이 무엇인지 먼저 확인하세요",
        body: [
          "TFN은 ATO가 세금과 연금 기록을 관리할 때 사용하는 개인 식별 번호입니다. 보통 9자리이며 이직, 개명, 다른 주로 이사하거나 해외로 나가더라도 같은 번호를 계속 사용합니다.",
          "TFN과 ABN은 용도가 다릅니다. 직원으로 급여를 받거나 개인 세금 업무를 처리할 때 사용하는 TFN이 사업 활동을 식별하는 ABN을 대신하지 않으며, 반대도 마찬가지입니다.",
        ],
        sourceIds: ["tfn-source-overview"],
      },
      {
        id: "choose-route",
        title: "2. 본인 상황에 맞는 신청 경로를 선택하세요",
        body: [
          "한국 여권을 가진 영주권자나 취업 권한이 있는 임시비자 소지자가 현재 호주에 있다면 permanent migrants and temporary visitors 전용 온라인 신청 경로를 먼저 확인하세요. 유효한 여권 또는 여행 서류와 비자 정보를 이용해 신원을 확인합니다.",
          "호주 여권이 있는 시민권자, 호주 여권이 없는 Australian resident for tax purposes, 호주 밖에서 신청하는 사람은 각각 신청 경로가 다릅니다. 세법상 거주자와 이민상 비자 상태는 같은 개념이 아닐 수 있으므로 ATO의 Apply for a TFN 안내에서 자신에게 해당하는 경로를 선택하세요.",
        ],
        checklist: [
          "현재 호주 안에 있는지",
          "호주 여권 또는 외국 여권을 사용하는지",
          "현재 비자에 유효한 취업 권한이 있는지",
          "세법상 Australian resident 신청 경로에 해당하는지",
          "과거에 TFN을 발급받은 적이 없는지",
        ],
        sourceIds: [
          "tfn-source-apply",
          "tfn-source-migrants",
          "tfn-source-residents",
        ],
      },
      {
        id: "apply-officially",
        title: "3. ATO 공식 경로에서 무료로 신청하세요",
        body: [
          "TFN 신청은 무료입니다. 검색 광고나 비공식 대행 페이지보다 ATO의 Apply for a TFN 페이지에서 출발하세요. 신청 과정에서 입력하는 이름, 생년월일, 여권과 비자 정보는 공식 서류의 표기와 일치해야 합니다.",
          "호주 여권이 없는 Australian resident 신청자는 상황에 따라 온라인 신청 후 참여 Australia Post 지점에서 신원 확인 인터뷰를 하거나, Services Australia 또는 종이 신청 경로를 이용할 수 있습니다. 종이 신청 시에는 ATO가 지정한 신원 증빙 요건을 따르고 원본 서류를 우편으로 보내지 마세요.",
        ],
        sourceIds: [
          "tfn-source-apply",
          "tfn-source-migrants",
          "tfn-source-residents",
        ],
      },
      {
        id: "start-work",
        title: "4. 취업했다면 TFN declaration을 처리하세요",
        body: [
          "새 직장을 시작하면 고용주는 급여에서 얼마의 세금을 원천징수할지 판단하기 위해 TFN declaration을 요청할 수 있습니다. 온라인 또는 종이 양식에서 자신의 세법상 거주 상태, tax-free threshold 신청 여부 등 질문을 사실대로 작성하세요.",
          "TFN을 신청했지만 아직 받지 못했다면 declaration에서 신청 중임을 알릴 수 있습니다. ATO 고용주 안내에 따르면 이 경우 일반적으로 28일 안에 TFN을 제공해야 하므로, 발급 통지를 받으면 안전한 방법으로 현재 고용주에게 전달하세요.",
        ],
        sourceIds: ["tfn-source-declaration"],
      },
      {
        id: "protect-and-recover",
        title: "5. 번호를 안전하게 보관하고 분실 시 기존 번호를 찾으세요",
        body: [
          "TFN은 신원정보의 중요한 일부입니다. ATO, 현재 고용주, 은행, 연금기금, 등록 세무사처럼 정당한 필요가 있는 기관이나 사람에게만 제공하고, 문자·이메일·소셜미디어로 갑자기 TFN을 요구받으면 먼저 상대방을 확인하세요.",
          "번호를 잊었다면 ATO 온라인 서비스가 연결된 myGov나 ATO 앱, ATO에서 받은 문서, 등록 세무사를 통해 먼저 찾아보세요. 이미 번호가 있다면 재신청하지 않습니다. 도용되었거나 오용이 의심되면 ATO에 가능한 한 빨리 신고하세요.",
        ],
        sourceIds: ["tfn-source-overview", "tfn-source-security"],
      },
    ],
    faqs: [
      {
        id: "tfn-cost",
        question: "TFN 신청 비용이 있나요?",
        answer:
          "없습니다. ATO의 공식 TFN 신청은 무료입니다. 비공식 유료 대행 서비스를 이용할 필요는 없습니다.",
        sourceIds: ["tfn-source-apply", "tfn-source-overview"],
      },
      {
        id: "tfn-vs-abn",
        question: "TFN과 ABN은 같은 번호인가요?",
        answer:
          "아닙니다. TFN은 개인의 세금·연금 기록에 사용하는 번호이고, ABN은 사업체나 단체를 식별하는 11자리 번호입니다.",
        sourceIds: ["tfn-source-overview"],
      },
      {
        id: "tfn-lifetime",
        question: "이직하거나 이름이 바뀌면 TFN을 다시 받아야 하나요?",
        answer:
          "아닙니다. TFN은 평생 유지됩니다. 이직·개명·이사·출국 후에도 같은 번호를 사용하며 필요한 경우 개인정보만 업데이트하세요.",
        sourceIds: ["tfn-source-overview"],
      },
      {
        id: "tfn-korean-passport",
        question: "한국 여권과 호주 취업 가능 비자가 있으면 어디서 신청하나요?",
        answer:
          "현재 호주에 있고 유효한 취업 권한이 있는 영주권자·임시비자 소지자라면 ATO의 permanent migrants and temporary visitors 전용 온라인 경로를 확인하세요.",
        sourceIds: ["tfn-source-migrants"],
      },
      {
        id: "tfn-work-before-arrival",
        question: "TFN이 아직 없어도 일을 시작할 수 있나요?",
        answer:
          "TFN 발급 전이라도 취업 자체가 자동으로 금지되는 것은 아닙니다. 고용주의 TFN declaration에서 신청 중임을 알리고, 일반적으로 28일 안에 발급된 번호를 제공하세요.",
        sourceIds: ["tfn-source-declaration"],
      },
      {
        id: "tfn-processing",
        question: "신청 후 바로 번호가 나오나요?",
        answer:
          "신청 경로와 신원 확인 상황에 따라 처리 시간이 달라질 수 있습니다. 신청을 중복으로 제출하지 말고 ATO가 안내한 기간을 기다린 뒤, 지연되면 공식 문의 경로로 확인하세요.",
        sourceIds: ["tfn-source-apply", "tfn-source-migrants"],
      },
      {
        id: "tfn-lost",
        question: "예전에 받은 TFN을 잃어버렸습니다.",
        answer:
          "새로 신청하지 마세요. ATO 온라인 서비스가 연결된 myGov, ATO 앱, 세금 관련 문서나 등록 세무사를 통해 기존 번호를 찾고, 찾을 수 없으면 ATO에 문의하세요.",
        sourceIds: ["tfn-source-overview", "tfn-source-security"],
      },
      {
        id: "tfn-sharing",
        question: "누구에게 TFN을 알려줘도 되나요?",
        answer:
          "아닙니다. ATO, 현재 고용주, 은행, 연금기금, 등록 세무사 등 정당한 이유로 필요한 곳에만 제공하세요. 상대방의 신원을 확인하지 못했다면 공유하지 마세요.",
        sourceIds: ["tfn-source-security"],
      },
    ],
    sourceIds: [
      "tfn-source-overview",
      "tfn-source-apply",
      "tfn-source-migrants",
      "tfn-source-residents",
      "tfn-source-declaration",
      "tfn-source-security",
    ],
    additionalChecks: [
      "ATO 자동 접근이 제한된 신청 페이지는 실제 신청 직전에 브라우저에서 최신 안내를 다시 확인하세요.",
      "세법상 거주 상태, 비자와 취업 권한에 따라 신청 경로가 달라질 수 있습니다.",
      "개인별 원천징수 결과와 처리 기간은 ATO 또는 등록 세무사에게 확인하세요.",
    ],
    disclaimer:
      "이 안내는 일반적인 생활정보이며 개인의 세무·법률 자문이 아닙니다. 세법상 거주 상태, 비자와 취업 권한, 원천징수 선택은 개인 상황에 따라 달라질 수 있으므로 중요한 결정은 ATO 공식 안내 또는 등록 세무사에게 확인하세요.",
  },
  {
    topicId: "renting",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "뉴사우스웨일스(NSW)",
    summary:
      "NSW에서 집을 빌릴 때는 매물과 총비용을 확인한 뒤 신청하고, 승인 후 계약서·Tenant Information Statement·condition report를 검토하세요. bond는 최대 4주치 임대료이며 Rental Bonds Online을 우선 이용할 수 있습니다. 입주 사진, 수리 요청, 납부 내역과 퇴거 기록을 남기면 분쟁을 줄이는 데 도움이 됩니다.",
    keyPoints: [
      "신청 전에 직접 매물을 점검하고 최소 주거 기준, 공과금, 교통과 총예산을 확인하세요.",
      "holding fee는 신청이 승인된 뒤에만 낼 수 있으며 최대 1주치 임대료입니다.",
      "계약에는 cooling-off 기간이 없으므로 서명 전에 모든 조항과 제공 문서를 읽으세요.",
      "rental bond는 최대 4주치 임대료이며 집주인이나 중개인은 Rental Bonds Online을 먼저 제안해야 합니다.",
      "입주 condition report를 실제 상태와 대조하고 사진·영상과 함께 기한 안에 돌려주세요.",
      "수리 요청은 서면으로 남기고, 해결되지 않아도 임대료를 임의로 중단하지 마세요.",
      "퇴거 notice 기간은 계약 유형과 종료 사유에 따라 다르므로 통지 직전에 공식 표를 확인하세요.",
    ],
    sections: [
      {
        id: "inspect-and-apply",
        title: "1. 매물을 점검하고 감당 가능한 비용으로 신청하세요",
        body: [
          "사진과 광고만 보고 결정하지 말고 가능하면 직접 inspection에 참석하세요. 문과 창문의 잠금, 물·전기·환기, 곰팡이와 누수, 난방·냉방, 휴대전화 수신, 주차와 대중교통을 확인하고 NSW 최소 주거 기준에 맞는지도 살펴보세요.",
          "신청서에는 신원, 소득, 임대 이력과 추천인 정보가 요구될 수 있습니다. 필요한 개인정보만 신뢰할 수 있는 경로로 제출하고 사본을 보관하세요. 신청이 승인되기 전에는 holding fee를 받을 수 없고, 승인 후 금액도 최대 1주치 임대료입니다.",
        ],
        checklist: [
          "주당 임대료와 공과금·이사비를 포함한 총예산",
          "최소 주거 기준과 눈에 보이는 하자",
          "교통, 주차, 인터넷과 휴대전화 수신",
          "신청서에 제공할 신원·소득·임대 이력 자료",
          "모든 납부 영수증과 신청 기록",
        ],
        sourceIds: ["renting-source-application"],
      },
      {
        id: "review-and-sign",
        title: "2. 계약서와 필수 문서를 읽은 뒤 서명하세요",
        body: [
          "계약은 서명하면 법적 구속력이 있고 일반적인 cooling-off 기간이 없습니다. 이름, 주소, 임대료, 납부 주기, 시작·종료일, 포함된 물품과 추가 조항을 확인하고 이해하지 못한 내용은 서명 전에 질문하세요.",
          "집주인이나 중개인은 계약 전후 정해진 절차에 따라 residential tenancy agreement, Tenant Information Statement와 condition report를 제공해야 합니다. 구두 약속이나 수리 합의도 계약서 또는 이메일로 남기고 서명된 계약서 사본을 보관하세요.",
        ],
        checklist: [
          "계약 당사자 이름과 임대 주소",
          "임대료, 납부 방법과 계약 기간",
          "특약, 포함 물품과 약속된 수리",
          "Tenant Information Statement 수령",
          "서명된 계약서와 condition report 사본 보관",
        ],
        sourceIds: [
          "renting-source-signing",
          "renting-source-tenant-statement",
        ],
      },
      {
        id: "bond-and-condition",
        title: "3. bond와 입주 상태를 별도로 증빙하세요",
        body: [
          "NSW rental bond는 최대 4주치 임대료입니다. 집주인이나 중개인은 bond를 받기 전에 NSW Fair Trading의 Rental Bonds Online을 첫 번째 선택지로 제안해야 하며, 온라인 이용이 어렵다면 허용된 다른 납부 절차와 영수증을 확인하세요.",
          "condition report를 방마다 실제 상태와 대조해 누락된 흠집, 얼룩, 파손, 곰팡이와 작동하지 않는 설비를 추가하세요. 날짜가 남는 사진·영상을 찍고 보고서에 안내된 기한 안에 서명해 돌려준 뒤 원본 파일과 제출 기록을 보관하세요.",
        ],
        checklist: [
          "bond 금액이 4주치 임대료를 넘지 않는지 확인",
          "Rental Bonds Online 초대와 납부 확인서 저장",
          "모든 방·벽·바닥·가전의 입주 사진 촬영",
          "condition report의 누락·오류 수정",
          "보고서 제출일과 이메일 기록 보관",
        ],
        sourceIds: ["renting-source-signing", "renting-source-bonds"],
      },
      {
        id: "manage-tenancy",
        title: "4. 임대료와 수리 기록을 계속 남기세요",
        body: [
          "임대료는 계약된 방법과 날짜에 맞춰 납부하고 은행 내역이나 영수증을 보관하세요. 고장이나 손상은 발견 즉시 사진과 함께 집주인 또는 중개인에게 서면으로 알리고, 언제 발견했는지와 요청한 조치를 구체적으로 적으세요.",
          "긴급 수리는 일반 수리와 절차가 다르므로 NSW 공식 목록과 계약서의 긴급 연락처를 먼저 확인하세요. 연락을 합리적으로 시도한 기록과 영수증을 남기고, 분쟁이 해결되지 않으면 NSW Fair Trading 민원이나 NCAT 절차를 검토하세요. 수리가 늦어져도 임대료를 임의로 중단해서는 안 됩니다.",
        ],
        sourceIds: ["renting-source-repairs"],
      },
      {
        id: "end-and-recover-bond",
        title: "5. 올바르게 통지하고 최종 상태를 기록하세요",
        body: [
          "퇴거 notice 기간과 방식은 fixed-term인지 periodic인지, 누가 어떤 사유로 계약을 끝내는지에 따라 달라집니다. 오래된 기억이나 비공식 표를 따르지 말고 통지 직전에 NSW Government의 현재 안내를 확인해 서면으로 보내고 전달 증거를 남기세요.",
          "퇴거일까지 개인 물품과 쓰레기를 치우고 합리적으로 청소한 뒤 모든 열쇠를 반환하세요. 입주 condition report와 비교해 최종 사진·영상을 찍고, final inspection과 bond 환급 절차를 기록하세요. 공제에 동의하지 않으면 사유와 증빙을 요청하고 관련 응답 기한을 놓치지 마세요.",
        ],
        checklist: [
          "계약 유형·종료 사유별 최신 notice 확인",
          "서면 notice와 전달 증거 보관",
          "청소·열쇠 반환과 최종 계량기 기록",
          "입주 자료와 같은 구도로 퇴거 사진 촬영",
          "bond 청구와 이견 제기 기한 확인",
        ],
        sourceIds: ["renting-source-ending"],
      },
    ],
    faqs: [
      {
        id: "renting-holding-fee",
        question: "신청서를 내기 전에 holding fee를 요구할 수 있나요?",
        answer:
          "아닙니다. holding fee는 신청이 승인된 뒤에만 받을 수 있고 최대 1주치 임대료입니다. 납부 전 승인 사실과 환불·계약 처리 조건을 서면으로 확인하세요.",
        sourceIds: ["renting-source-application"],
      },
      {
        id: "renting-starting-costs",
        question: "입주 전에 어떤 비용을 요구할 수 있나요?",
        answer:
          "NSW 공식 안내는 tenancy 시작 때 받을 수 있는 항목을 holding fee, rental bond, rent와 해당되는 lease 등록비로 제한합니다. 명목이 불분명한 신청비나 추가비는 납부 전에 근거를 요청하세요.",
        sourceIds: ["renting-source-application"],
      },
      {
        id: "renting-bond-limit",
        question: "bond는 얼마까지 낼 수 있나요?",
        answer:
          "최대 4주치 임대료입니다. Rental Bonds Online으로 직접 납부하면 NSW Fair Trading 시스템에서 기록을 확인할 수 있습니다.",
        sourceIds: ["renting-source-bonds"],
      },
      {
        id: "renting-rbo",
        question: "Rental Bonds Online을 꼭 이용해야 하나요?",
        answer:
          "집주인이나 중개인은 bond를 받기 전에 Rental Bonds Online을 첫 번째 선택지로 제안해야 합니다. 온라인 이용이 어렵다면 공식적으로 허용된 다른 절차와 영수증 발급 방식을 확인하세요.",
        sourceIds: ["renting-source-bonds"],
      },
      {
        id: "renting-condition-report",
        question: "condition report가 실제 상태와 다르면 어떻게 하나요?",
        answer:
          "누락된 하자와 다른 내용을 직접 추가하고 날짜가 남는 사진·영상으로 증빙하세요. 보고서에 적힌 제출 기한 안에 돌려주고 사본과 제출 기록을 보관하세요.",
        sourceIds: ["renting-source-signing"],
      },
      {
        id: "renting-repairs",
        question: "수리를 해주지 않으면 임대료를 내지 않아도 되나요?",
        answer:
          "임대료를 임의로 중단하면 안 됩니다. 서면 요청과 연락 기록을 남기고 긴급 수리 절차를 확인한 뒤, 해결되지 않으면 NSW Fair Trading 또는 NCAT 경로를 검토하세요.",
        sourceIds: ["renting-source-repairs"],
      },
      {
        id: "renting-no-cooling-off",
        question: "계약서에 서명한 뒤 마음이 바뀌면 취소할 수 있나요?",
        answer:
          "일반적인 cooling-off 기간이 없고 계약은 구속력이 있습니다. 서명 전 모든 조항을 읽고, 서명 후 나가려면 계약 조기 종료에 따른 비용과 절차를 공식 안내에서 확인하세요.",
        sourceIds: ["renting-source-tenant-statement"],
      },
      {
        id: "renting-notice-period",
        question: "이사 나갈 때 며칠 전에 알려야 하나요?",
        answer:
          "계약 유형과 종료 사유에 따라 notice 기간이 달라 한 숫자로 답할 수 없습니다. 통지 직전에 NSW Government의 현재 안내에서 자신의 상황을 선택해 확인하고 서면 증거를 남기세요.",
        sourceIds: ["renting-source-ending"],
      },
      {
        id: "renting-bond-dispute",
        question: "퇴거 후 bond 공제에 동의하지 않으면 어떻게 하나요?",
        answer:
          "공제 사유와 영수증·사진 등 증빙을 요청하고 입주·퇴거 자료와 비교하세요. Rental Bonds Online의 진행 상태와 이견 제기 기한을 확인하고 필요하면 NSW Fair Trading 또는 NCAT 안내를 이용하세요.",
        sourceIds: ["renting-source-bonds", "renting-source-ending"],
      },
    ],
    sourceIds: [
      "renting-source-application",
      "renting-source-signing",
      "renting-source-tenant-statement",
      "renting-source-bonds",
      "renting-source-repairs",
      "renting-source-ending",
    ],
    additionalChecks: [
      "서명 전 계약서의 기간, 특약, 약속된 수리와 비용을 개별적으로 확인하세요.",
      "퇴거 통지, 임대료 인상, 긴급 수리와 bond 이견 제기 기한은 행동 직전에 NSW 공식 안내에서 다시 확인하세요.",
      "반려동물, 공동주택 by-laws, 전대와 시설 변경은 계약서와 적용 규칙을 별도로 확인하세요.",
      "분쟁 금액이 크거나 퇴거 위험이 있으면 NSW tenant advice 서비스나 법률 전문가의 조언을 받으세요.",
    ],
    disclaimer:
      "이 안내는 NSW 주거 임대에 관한 일반 생활정보이며 개인의 법률 자문이 아닙니다. 계약 내용, 계약 유형, 종료 사유와 분쟁 상황에 따라 적용 절차와 기한이 달라질 수 있으므로 중요한 행동 전 NSW Fair Trading·NSW Government 공식 안내 또는 tenant advice 서비스에 확인하세요.",
  },
  {
    topicId: "jobs",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R1",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "호주 전역",
    summary:
      "호주 구직은 지원 수를 늘리는 것보다 공고에 맞춘 이력서와 cover letter, 고용주 진위 확인, 급여·고용형태·근무시간의 서면 확인이 중요합니다. 채용 후에는 적용 award와 급여명세서를 확인하고 실제 근무시간을 기록해 두세요.",
    keyPoints: [
      "공고의 핵심 업무와 selection criteria에 맞춰 이력서와 cover letter를 매번 조정하세요.",
      "회사 공식 웹사이트와 직접 찾은 연락처로 채용 담당자와 공고가 진짜인지 확인하세요.",
      "일을 시작하기 위해 돈이나 암호화폐를 보내라는 제안은 중단하고 Scamwatch 경고 신호를 확인하세요.",
      "급여, 고용형태, 근무시간, 근무지, 시작일과 적용 award를 수락 전에 서면으로 확인하세요.",
      "짧은 기술 시연을 넘어 실제 생산적인 업무를 하는 trial은 유급이어야 할 가능성이 큽니다.",
      "비자 소지자도 동일한 직장 권리를 가지지만 본인의 근무 가능 조건은 VEVO에서 따로 확인해야 합니다.",
      "첫날부터 실제 근무시간과 급여를 기록하고 매 pay day의 payslip을 확인하세요.",
    ],
    sections: [
      {
        id: "set-target",
        title: "1. 지원할 직무와 조건을 먼저 정하세요",
        body: [
          "원하는 직무명만 정하지 말고 통근 가능 지역, 근무 가능 시간, full-time·part-time·casual 선호, 필요한 자격증과 최소 급여 조건을 적어 보세요. 비슷한 공고 여러 개에서 반복되는 업무와 기술을 모으면 어떤 경험을 이력서 앞부분에 배치할지 판단하기 쉽습니다.",
          "비자 소지자는 지원 전에 VEVO에서 현재 근무 권한과 제한을 확인하세요. 호주에서 일하는 migrant worker도 다른 직원과 같은 최저 근로조건과 보호를 받지만, 직종·시간 등 비자 조건은 별도로 지켜야 합니다.",
        ],
        checklist: [
          "희망 직무명과 검색 키워드",
          "통근 지역과 근무 가능 시간",
          "full-time·part-time·casual 선호",
          "보유 자격증과 추가로 필요한 자격",
          "현재 비자의 근무 권한과 제한",
        ],
        sourceIds: ["jobs-source-applications", "jobs-source-migrant-rights"],
      },
      {
        id: "tailor-application",
        title: "2. 공고에 맞춰 지원서를 조정하세요",
        body: [
          "이력서는 최근의 관련 경험, 업무 성과, 기술과 자격을 읽기 쉽게 정리하고 공고의 표현과 맞는 키워드를 사용하세요. 사실과 다른 경력이나 자격을 추가하지 말고, 주소·생년월일·신분증 번호처럼 초기 지원에 불필요한 민감정보는 넣지 않는 편이 안전합니다.",
          "cover letter는 지원 직무, 관심 이유, 가장 관련 있는 경험과 근무 가능 시기를 짧게 연결하세요. 같은 문서를 모든 회사에 보내기보다 파일명과 수신인, 직무명을 확인하고 PDF 또는 요청된 형식으로 제출한 뒤 공고와 제출본을 보관하세요.",
        ],
        checklist: [
          "공고의 핵심 업무·필수 조건 표시",
          "관련 경험과 측정 가능한 성과를 앞쪽에 배치",
          "직무별 이력서와 한 페이지 이내 cover letter 준비",
          "연락 가능한 referee에게 사전 동의 받기",
          "공고, 제출일, 문서 버전과 결과 기록",
        ],
        sourceIds: ["jobs-source-applications"],
      },
      {
        id: "verify-and-interview",
        title: "3. 회사와 공고를 확인하고 면접을 준비하세요",
        body: [
          "신뢰할 만한 구인 플랫폼에 올라온 공고도 가짜일 수 있습니다. 회사 웹사이트, ABN과 공개 연락처를 독립적으로 찾아 확인하고, 문자나 WhatsApp으로 갑자기 접근해 빠른 결정을 재촉하거나 면접 없이 고수익을 보장하면 멈추세요. 일을 받기 위해 recruitment fee, training fee, 계좌 충전 또는 암호화폐를 보내지 마세요.",
          "면접 전 공고와 회사 업무를 다시 읽고 자신의 경험을 구체적인 사례로 설명하는 연습을 하세요. 면접에서는 실제 업무, 팀, roster, 급여 범위, 고용형태, 근무지와 다음 절차를 확인하되 신분증·은행정보는 고용주 진위와 필요성을 확인하기 전에 보내지 마세요.",
        ],
        sourceIds: ["jobs-source-interview", "jobs-source-scams"],
      },
      {
        id: "review-offer",
        title: "4. 제안을 서면으로 받고 조건을 비교하세요",
        body: [
          "구두로 합의했더라도 직무명, 고용형태, base rate 또는 salary, 예상 근무시간, 근무지, 시작일, probation과 적용 award·enterprise agreement를 서면으로 요청하세요. 계약서가 법정 최저 권리보다 낮은 조건을 적었다고 해서 그 최저 권리가 사라지지는 않습니다.",
          "unpaid trial은 지원자의 기술을 보여주는 데 필요한 정도로 짧고 직접적인 평가여야 합니다. 실제 고객 업무나 정상 roster를 대신하는 생산적인 일을 요구하거나 trial이 길어지면 유급 고용관계일 수 있으므로 시작 전에 시간과 업무, 지급 여부를 확인하세요.",
        ],
        checklist: [
          "직무명과 실제 업무 범위",
          "full-time·part-time·casual·fixed-term 여부",
          "급여율, 지급 주기, penalty rate와 allowance",
          "근무시간·roster·휴식과 근무지",
          "probation, 시작일과 적용 award 또는 agreement",
          "trial의 시간, 평가 업무와 지급 여부",
        ],
        sourceIds: ["jobs-source-before-starting", "jobs-source-unpaid-work"],
      },
      {
        id: "start-and-record",
        title: "5. 첫날부터 시간·급여·서류를 기록하세요",
        body: [
          "고용주가 요청하면 급여와 세금·super 처리를 위한 정확한 개인정보를 안전한 방식으로 제출하세요. 첫날부터 시작·종료 시각, 휴식, training과 추가 근무를 본인이 따로 기록하고 약속된 roster 및 급여율과 비교하세요.",
          "고용주는 pay day 뒤 1 working day 안에 payslip을 제공해야 합니다. gross와 net pay, 근무시간, 급여율, loading·allowance·deduction과 super 정보를 확인하고 누락이나 차이가 있으면 먼저 서면으로 문의하세요. 해결되지 않으면 Fair Work Ombudsman의 무료 안내를 이용할 수 있습니다.",
        ],
        sourceIds: ["jobs-source-before-starting", "jobs-source-migrant-rights"],
      },
    ],
    faqs: [
      {
        id: "jobs-resume-length",
        question: "호주 이력서는 반드시 한 페이지여야 하나요?",
        answer:
          "고정된 한 페이지 규칙은 없습니다. 경력 수준과 직무에 맞게 관련 정보만 간결하게 정리하고, 공고의 핵심 기술과 성과를 쉽게 찾을 수 있도록 구성하세요.",
        sourceIds: ["jobs-source-applications"],
      },
      {
        id: "jobs-cover-letter",
        question: "모든 지원에 cover letter가 필요한가요?",
        answer:
          "공고에서 제출하지 말라고 하지 않는 한 준비하는 것이 좋습니다. 직무명, 지원 이유, 관련 경험과 근무 가능 시기를 공고에 맞춰 짧게 작성하세요.",
        sourceIds: ["jobs-source-applications"],
      },
      {
        id: "jobs-personal-details",
        question: "이력서에 생년월일과 여권번호를 넣어야 하나요?",
        answer:
          "일반적인 초기 지원에는 필요하지 않습니다. 주소, 생년월일, 신분증 번호 같은 민감정보는 최소화하고 고용주와 요청 목적을 확인한 뒤 필요한 단계에서만 안전하게 제공하세요.",
        sourceIds: ["jobs-source-scams"],
      },
      {
        id: "jobs-upfront-fee",
        question: "채용을 위해 교육비나 보증금을 먼저 보내라고 합니다.",
        answer:
          "송금하지 마세요. 선불 비용, 계좌 충전이나 암호화폐를 요구하는 고수익 제안은 대표적인 job scam 신호입니다. 회사의 공식 연락처로 독립적으로 확인하고 Scamwatch에 신고하세요.",
        sourceIds: ["jobs-source-scams"],
      },
      {
        id: "jobs-unpaid-trial",
        question: "무급 trial shift는 항상 합법인가요?",
        answer:
          "아닙니다. 기술 확인에 필요한 짧은 시연은 무급일 수 있지만, 실제 생산적인 업무를 하거나 평가에 필요한 시간보다 길다면 유급 고용관계일 가능성이 있습니다. 업무와 시간을 시작 전에 서면으로 확인하세요.",
        sourceIds: ["jobs-source-before-starting", "jobs-source-unpaid-work"],
      },
      {
        id: "jobs-minimum-pay",
        question: "최저시급만 확인하면 제 급여를 알 수 있나요?",
        answer:
          "대부분의 직원은 산업이나 직종별 award가 적용되므로 하나의 전국 숫자만으로 판단하면 안 됩니다. Fair Work의 Pay and Conditions Tool에서 고용형태, 나이, 직무, 근무시간에 맞는 rate와 penalty·allowance를 확인하세요.",
        sourceIds: ["jobs-source-before-starting"],
      },
      {
        id: "jobs-cash-pay",
        question: "현금으로 급여를 받으면 권리가 없나요?",
        answer:
          "지급 수단과 관계없이 직원의 최저 권리는 적용됩니다. 실제 근무시간과 받은 금액을 기록하고 payslip, 세금 공제와 super 처리가 맞는지 확인하세요.",
        sourceIds: ["jobs-source-migrant-rights"],
      },
      {
        id: "jobs-visa-rights",
        question: "학생비자나 임시비자 소지자는 Fair Work에 도움을 요청할 수 있나요?",
        answer:
          "네. migrant worker도 호주에서 다른 직원과 같은 직장 권리와 보호를 받으며, Fair Work에 문의했다고 고용주가 비자를 취소할 수 없습니다. 다만 본인의 비자 근무 조건은 VEVO에서 별도로 확인하세요.",
        sourceIds: ["jobs-source-migrant-rights"],
      },
      {
        id: "jobs-payslip",
        question: "급여를 받았는데 payslip이 없습니다.",
        answer:
          "고용주는 pay day 뒤 1 working day 안에 종이 또는 전자 payslip을 제공해야 합니다. 먼저 서면으로 요청하고 계속 제공하지 않거나 금액이 맞지 않으면 Fair Work Ombudsman 안내를 이용하세요.",
        sourceIds: ["jobs-source-before-starting"],
      },
    ],
    sourceIds: [
      "jobs-source-applications",
      "jobs-source-interview",
      "jobs-source-before-starting",
      "jobs-source-unpaid-work",
      "jobs-source-migrant-rights",
      "jobs-source-scams",
    ],
    additionalChecks: [
      "지원 전 회사와 채용 담당자의 진위를 독립적인 공식 연락처로 확인하세요.",
      "제안을 수락하기 전에 현재 적용 award, 급여율, penalty와 allowance를 Fair Work 도구에서 다시 확인하세요.",
      "비자 소지자는 직종·시간·고용주 관련 현재 근무 조건을 VEVO에서 확인하세요.",
      "차별, 미지급 임금, 강요 또는 해고 위험이 있으면 관련 기록을 보관하고 Fair Work 또는 전문기관에 개별 상담을 요청하세요.",
    ],
    disclaimer:
      "이 안내는 호주 구직과 근무 시작에 관한 일반 생활정보이며 개인의 법률·이민 자문이 아닙니다. 실제 권리는 고용형태, award·agreement, 나이, 직무와 비자 조건에 따라 달라질 수 있으므로 중요한 결정은 Fair Work Ombudsman, Home Affairs 또는 적절한 전문가에게 확인하세요.",
  },
  {
    topicId: "nsw-driver-licence",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "뉴사우스웨일스(NSW)",
    summary:
      "한국 운전면허로 NSW에서 운전할 수 있는 기간은 비자·거주 상태에 따라 일반적으로 3개월 또는 6개월입니다. 2026년 2월 1일부터 한국 면허의 경력자 시험 면제가 NSW에서 종료되어, 현재 전환 시 knowledge test와 driving test가 필요합니다. 기한이 끝나기 전에 서류와 시험 일정을 준비하세요.",
    keyPoints: [
      "영주권자·호주 시민권자는 일반적으로 입국 후 3개월 안에 NSW 면허로 전환해야 계속 운전할 수 있습니다.",
      "2023년 7월 1일 이후 NSW에 온 임시비자 소지자는 일반적으로 NSW 거주 6개월 안에 전환해야 계속 운전할 수 있습니다.",
      "한국어 면허만 소지했다면 운전할 때 허용된 영어 번역 또는 International Driving Permit도 함께 휴대하세요.",
      "2026년 2월 1일부터 한국 면허 소지자의 기존 knowledge·driving test 면제가 종료됐습니다.",
      "한국 면허 원본, 신분증, 비자·영주권 증빙, 허용된 원본 번역과 신청서를 준비하세요.",
      "한국 면허 보유 기간에 따라 NSW P1·P2·unrestricted 중 전환 가능한 등급이 달라집니다.",
      "driving test에 불합격하면 해외면허로 운전하던 visiting privileges도 끝날 수 있으므로 귀가 방법까지 준비하세요.",
    ],
    sections: [
      {
        id: "check-deadline",
        title: "1. 먼저 해외면허 사용 가능 기한을 계산하세요",
        body: [
          "현재 한국 면허가 유효하고 정지·취소되지 않았으며 NSW에서 운전 자격이 철회되지 않았다면 제한 기간 동안 사용할 수 있습니다. 운전할 때는 면허 원본을 휴대하고, 영어가 아닌 면허라면 허용된 영어 번역 또는 발급국의 International Driving Permit도 함께 지녀야 합니다.",
          "호주 시민권자·영주권자는 일반적으로 호주 도착일부터 3개월 안에 NSW 면허로 전환해야 계속 운전할 수 있습니다. 2023년 7월 1일 이후 NSW에 와서 계속 거주하는 임시비자 소지자는 일반적으로 6개월 안에 임시 NSW 면허를 받아야 합니다. 여행이나 다른 주 방문이 자동으로 거주 기간을 다시 시작시키는 것은 아니므로 날짜를 임의로 계산하지 마세요.",
        ],
        checklist: [
          "현재 비자 또는 영주권·시민권 상태",
          "호주 입국일과 NSW를 주 거주지로 삼은 날짜",
          "한국 면허 유효기간과 정지·취소 여부",
          "영어 번역 또는 International Driving Permit 보유 여부",
          "개인별 전환 마감일을 달력에 기록",
        ],
        sourceIds: [
          "nsw-driver-source-overseas-driving",
          "nsw-driver-source-temporary",
        ],
      },
      {
        id: "check-tests-and-class",
        title: "2. 필요한 시험과 전환 면허 등급을 확인하세요",
        body: [
          "과거에는 만 25세 이상 한국 면허 소지자가 NSW의 Experienced Driver Recognition에 따라 시험을 면제받는 경우가 있었지만, 이 제도는 2026년 2월 1일부터 NSW에서 종료됐습니다. 현재 한국은 시험 면제 국가 목록에 없으므로 일반적으로 Driver Knowledge Test와 practical driving test가 필요합니다.",
          "한국 면허 보유 기간도 중요합니다. 일반적으로 12개월 미만이면 P1, 12개월 이상 3년 미만이면 P2, 3년 이상이면 unrestricted licence에 해당할 수 있지만, 면허 종류·유효성·운전 이력에 따라 달라집니다. 시험을 예약하기 전에 Service NSW에 본인의 요구 시험과 예상 등급을 확인하세요.",
        ],
        sourceIds: [
          "nsw-driver-source-exemptions",
          "nsw-driver-source-eligibility",
        ],
      },
      {
        id: "prepare-documents",
        title: "3. 원본 서류와 허용된 번역을 준비하세요",
        body: [
          "Licence Application, proof of identity, 한국 면허 원본, 비자 또는 영주권 관련 증빙과 결제수단을 준비하세요. 한국 면허를 제시할 수 없다면 발급기관의 영문 authorisation letter가 필요할 수 있고, 일부 신청자는 validation letter나 medical assessment를 추가로 요구받을 수 있습니다.",
          "한국어 면허의 번역은 Transport for NSW가 인정하는 기관의 원본이어야 합니다. Service NSW는 Multicultural NSW, Home Affairs의 Translating and Interpreting Service, 그리고 한국에서 발급된 면허의 경우 주시드니 대한민국 총영사관 번역을 허용된 경로로 안내합니다. 복사본이 아닌 원본 번역을 준비하세요.",
        ],
        checklist: [
          "작성한 Licence Application",
          "요건에 맞는 proof of identity",
          "유효한 한국 운전면허 원본",
          "비자 또는 영주권·시민권 증빙",
          "Transport for NSW가 인정하는 영어 번역 원본",
          "필요한 경우 validation letter·medical assessment·안경",
          "시험비와 면허 발급비 결제수단",
        ],
        sourceIds: [
          "nsw-driver-source-transfer",
          "nsw-driver-source-temporary",
        ],
      },
      {
        id: "tests-and-visit",
        title: "4. 시험과 Service NSW 방문 순서를 계획하세요",
        body: [
          "Service NSW에서 요구 시험을 확인한 뒤 NSW Road User Handbook과 공식 연습자료로 준비하세요. knowledge test와 driving test 예약 가능 시점이 다를 수 있으므로 해외면허 사용기한 직전까지 미루지 말고, 시험 당일 필요한 신분증·면허·번역·신청서와 적합한 시험 차량을 다시 확인하세요.",
          "서비스 센터에서는 서류 확인, 시력검사와 사진 촬영을 진행하며 필요한 시험을 통과해야 합니다. 특히 driving test에 불합격하면 해외면허에 따른 visiting driver privileges가 더 이상 적용되지 않을 수 있습니다. 시험장까지 직접 운전했다면 불합격 시 다른 적법한 운전자가 차량을 가져갈 수 있도록 준비하세요.",
        ],
        sourceIds: [
          "nsw-driver-source-exemptions",
          "nsw-driver-source-service-centre",
        ],
      },
      {
        id: "after-issue",
        title: "5. NSW 면허 발급 후 조건을 확인하세요",
        body: [
          "신청이 승인되면 카드가 도착할 때까지 사용할 수 있는 임시 종이면허를 받을 수 있습니다. 카드의 이름, 주소, 면허 등급, expiry와 Q 조건 등 표시가 맞는지 확인하고, P1·P2 또는 임시면허에 붙은 속도·표시판·휴대전화 등 조건을 따르세요.",
          "NSW 면허가 발급되면 NSW 도로에서 운전 권한을 증명하는 면허는 NSW 면허가 됩니다. 기존 해외면허를 근거로 다시 운전할 수 없으므로 갱신·주소 변경과 비자 상태 변경에 따른 Q 조건 제거 절차도 NSW 기준으로 처리하세요.",
        ],
        sourceIds: [
          "nsw-driver-source-temporary",
          "nsw-driver-source-service-centre",
        ],
      },
    ],
    faqs: [
      {
        id: "nsw-driver-korean-licence-period",
        question: "한국 면허로 NSW에서 얼마나 운전할 수 있나요?",
        answer:
          "호주 시민권자·영주권자는 일반적으로 도착 후 3개월, 2023년 7월 1일 이후 NSW에 와서 거주하는 임시비자 소지자는 일반적으로 6개월입니다. 입국일과 실제 NSW 거주 시작일, 비자 상태에 따라 달라질 수 있으므로 Service NSW에서 개인별 기한을 확인하세요.",
        sourceIds: [
          "nsw-driver-source-overseas-driving",
          "nsw-driver-source-temporary",
        ],
      },
      {
        id: "nsw-driver-translation",
        question: "한국 면허증만 들고 운전해도 되나요?",
        answer:
          "면허가 영어로 작성되지 않았다면 허용된 영어 번역 또는 International Driving Permit을 면허 원본과 함께 휴대해야 합니다. 전환 신청에는 Transport for NSW가 인정하는 번역 원본을 준비하세요.",
        sourceIds: [
          "nsw-driver-source-overseas-driving",
          "nsw-driver-source-transfer",
        ],
      },
      {
        id: "nsw-driver-korea-exemption",
        question: "만 25세 이상이면 한국 면허를 시험 없이 바꿀 수 있나요?",
        answer:
          "현재는 일반적으로 불가능합니다. 한국을 포함한 기존 경력자 인정 국가의 NSW 시험 면제가 2026년 2월 1일부터 종료되어 knowledge test와 driving test가 필요합니다. 예약 전 현재 면제 목록을 다시 확인하세요.",
        sourceIds: ["nsw-driver-source-exemptions"],
      },
      {
        id: "nsw-driver-licence-class",
        question: "한국 운전경력을 모두 인정받아 full licence를 받나요?",
        answer:
          "자동으로 full licence가 되는 것은 아닙니다. 일반적으로 해외면허 보유 기간이 3년 이상이면 unrestricted 등급에 해당할 수 있지만, 면허 유효성·종류·운전 이력과 시험 결과를 Service NSW가 확인해 결정합니다.",
        sourceIds: ["nsw-driver-source-eligibility"],
      },
      {
        id: "nsw-driver-temporary-q",
        question: "임시비자 소지자가 받는 Q 면허는 무엇인가요?",
        answer:
          "영주권 증빙을 제공하지 않은 temporary overseas visitor의 NSW 면허에는 Q가 표시됩니다. 이후 시민권자나 영주권자가 되면 Service NSW Centre에서 증빙을 제출해 Q 조건 제거를 신청할 수 있습니다.",
        sourceIds: ["nsw-driver-source-temporary"],
      },
      {
        id: "nsw-driver-consulate-translation",
        question: "한국 총영사관 번역도 인정되나요?",
        answer:
          "네. Service NSW는 대한민국에서 발급된 운전면허에 대해 주시드니 대한민국 총영사관의 번역을 허용된 경로 중 하나로 안내합니다. 전환 신청에는 번역 원본을 제출해야 합니다.",
        sourceIds: ["nsw-driver-source-transfer"],
      },
      {
        id: "nsw-driver-failed-test",
        question: "driving test에 떨어져도 한국 면허로 계속 운전할 수 있나요?",
        answer:
          "그렇지 않을 수 있습니다. 해외면허 전환을 위한 driving test에 불합격하면 visiting driver privileges가 끝날 수 있으므로, 시험 후 직접 운전하지 말고 Service NSW의 안내에 따라 learner licence와 재시험 절차를 확인하세요.",
        sourceIds: ["nsw-driver-source-service-centre"],
      },
      {
        id: "nsw-driver-expired-korean-licence",
        question: "한국 면허가 만료됐어도 전환할 수 있나요?",
        answer:
          "면허의 만료 시점과 검증 가능 여부에 따라 다릅니다. NSW 등급 인정과 시험 요건이 달라질 수 있으므로 authorisation 또는 validation 자료가 필요한지 Service NSW에 먼저 확인하세요.",
        sourceIds: [
          "nsw-driver-source-transfer",
          "nsw-driver-source-eligibility",
        ],
      },
      {
        id: "nsw-driver-medical",
        question: "건강 상태나 안경 사용을 알려야 하나요?",
        answer:
          "네. 신청 과정에서 시력검사를 받고 안경이나 콘택트렌즈를 사용한다면 가져가야 합니다. 운전에 영향을 줄 수 있는 건강 상태가 있거나 연령 요건에 해당하면 medical assessment나 추가 시험이 필요할 수 있습니다.",
        sourceIds: [
          "nsw-driver-source-transfer",
          "nsw-driver-source-service-centre",
        ],
      },
    ],
    sourceIds: [
      "nsw-driver-source-overseas-driving",
      "nsw-driver-source-temporary",
      "nsw-driver-source-transfer",
      "nsw-driver-source-exemptions",
      "nsw-driver-source-eligibility",
      "nsw-driver-source-service-centre",
    ],
    additionalChecks: [
      "입국일·NSW 거주 시작일·비자 상태에 따른 해외면허 사용 종료일을 Service NSW에서 확인하세요.",
      "2026년 2월 1일 이후의 현재 인정 국가 목록과 본인에게 필요한 시험을 예약 전에 다시 확인하세요.",
      "한국 면허 validation letter와 번역 원본의 요구 형식은 방문할 Service NSW Centre에 확인하세요.",
      "면허 발급비, 시험비, 예약 가능일과 시험 차량 요건은 결제·예약 직전에 공식 페이지에서 확인하세요.",
      "정지·취소·벌점·건강 상태 또는 불합격 이력이 있다면 일반 안내만 따르지 말고 Service NSW에 개별 확인하세요.",
    ],
    disclaimer:
      "이 안내는 NSW 운전면허와 한국 면허 전환에 관한 일반 생활정보이며 개인의 법률 자문이 아닙니다. 비자 상태, 입국·거주일, 면허 종류와 이력, 건강 상태 및 규정 변경에 따라 운전 가능 여부가 달라질 수 있으므로 운전이나 시험 예약 전 Service NSW 또는 Transport for NSW에 확인하세요.",
  },
  {
    topicId: "medicare",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "호주 전역",
    summary:
      "Medicare는 호주의 공공 의료보험 제도입니다. 한국 국적만으로 자동 가입되는 것은 아니며, 호주 시민권·영주권 또는 자격이 되는 영주권 신청 상태 등 개인의 체류 상황을 기준으로 신청해야 합니다. 승인 후 myGov 연결과 은행정보를 확인하고 진료 전 bulk billing과 본인부담금을 물어보세요.",
    keyPoints: [
      "한국은 현재 호주의 Reciprocal Health Care Agreement 대상 국가가 아닙니다.",
      "호주 시민권자·영주권자와 일부 영주권 또는 보호비자 신청자는 Medicare에 가입할 수 있습니다.",
      "영주권을 신청했다는 사실만으로 모두 가입되는 것은 아니며 비자의 근무 권한이나 가족관계 조건이 추가될 수 있습니다.",
      "현재 여권 또는 ImmiCard, Home Affairs의 비자·영주권 신청 증빙을 가족 구성원별로 준비하세요.",
      "myGov 온라인 신청이 가능하지 않으면 Medicare enrolment form과 서류를 이메일이나 우편으로 제출할 수 있습니다.",
      "Medicare 카드가 있어도 모든 진료가 무료인 것은 아니므로 bulk billing과 예상 gap을 예약 전에 확인하세요.",
      "Medicare 환급을 받으려면 온라인 계정의 본인 은행정보가 정확한지 확인하세요.",
    ],
    sections: [
      {
        id: "check-eligibility",
        title: "1. 국적이 아니라 현재 자격으로 가입 가능 여부를 확인하세요",
        body: [
          "호주에 거주하는 Australian citizen, New Zealand citizen, Australian permanent resident, 자격이 되는 permanent residency 신청자 또는 Ministerial Order 대상 임시거주자는 Medicare 가입 대상이 될 수 있습니다. 단순한 임시비자, 학생비자 또는 한국 여권만으로 자동 가입되는 것은 아닙니다.",
          "호주는 일부 국가 방문자에게 제한적인 의료 혜택을 제공하는 Reciprocal Health Care Agreement를 운영하지만 현재 공식 11개국 목록에 한국은 포함되지 않습니다. 따라서 한국에서 온 방문자나 일반 임시비자 소지자는 자신의 비자 자격을 확인하고 Medicare 대상이 아니라면 적절한 private health insurance·OSHC·OVHC 보장을 별도로 점검하세요.",
        ],
        checklist: [
          "현재 호주 내 거주 여부",
          "시민권·영주권 또는 현재 비자 종류",
          "영주권·보호비자 신청 여부와 신청일",
          "현재 비자의 근무 권한",
          "호주 시민권자·영주권자·거주 중인 뉴질랜드 시민 가족 여부",
          "현재 민간 의료보험의 시작일·종료일과 보장 범위",
        ],
        sourceIds: [
          "medicare-source-enrolment",
          "medicare-source-permanent-resident",
          "medicare-source-rhca",
        ],
      },
      {
        id: "prepare-documents",
        title: "2. 가족 구성원별 신분·거주·비자 서류를 준비하세요",
        body: [
          "필요 서류는 자격 유형에 따라 다릅니다. 영주권 신청자는 일반적으로 현재 여권 또는 ImmiCard와 Home Affairs가 발급한 permanent residency 신청 증빙이 필요합니다. 함께 등록할 가족 구성원 각각의 신분서류를 준비하고 이름·생년월일·비자 정보가 일치하는지 확인하세요.",
          "현재 비자에 근무 권한이 없다면 Australian citizen, permanent resident 또는 호주에 거주하는 New Zealand citizen인 부모·배우자·사실혼 파트너·자녀와의 관계를 증명하는 추가 서류가 필요할 수 있습니다. 서류가 부족하면 임의의 대체자료를 제출하기보다 Services Australia에 가능한 다른 증빙을 문의하세요.",
        ],
        checklist: [
          "각 신청자의 현재 passport 또는 ImmiCard",
          "Home Affairs의 visa grant·PR application 증빙",
          "필요한 경우 birth·marriage certificate 등 가족관계 증빙",
          "가족 공동 신청 시 15세 미만 자녀의 신분서류",
          "myGov·여권·비자에 기록된 이름과 생년월일 일치 여부",
          "연락 가능한 호주 주소, 이메일과 전화번호",
        ],
        sourceIds: [
          "medicare-source-enrolment",
          "medicare-source-documents",
        ],
      },
      {
        id: "submit-application",
        title: "3. myGov 또는 Medicare enrolment form으로 신청하세요",
        body: [
          "온라인 자격을 충족하면 myGov에서 개인 또는 가족 Medicare enrolment를 제출하고 진행 상태를 확인할 수 있습니다. 가족 중 만 15세 이상인 사람을 함께 신청하는 경우 신청번호를 전달해 그 사람이 자신의 myGov 계정에서 신분정보를 완료해야 할 수 있습니다.",
          "온라인 신청이 되지 않으면 Medicare enrolment form을 작성해 지원서류와 함께 Medicare Enrolment Services에 이메일 또는 우편으로 제출할 수 있습니다. 공식 안내에 따르면 지원서류를 별도로 공증받을 필요는 없지만 파일 형식·크기와 이메일 제목 요구사항을 확인하고, 제출한 양식과 서류 사본을 안전하게 보관하세요.",
        ],
        sourceIds: [
          "medicare-source-enrolment",
          "medicare-source-permanent-resident",
        ],
      },
      {
        id: "set-up-card-and-account",
        title: "4. 승인 후 카드·myGov·은행정보를 확인하세요",
        body: [
          "가입이 승인되면 등록 유형에 따라 green, blue interim 또는 yellow RHCA 카드가 발급될 수 있습니다. 우편 카드가 도착하기 전에도 myGov 앱에서 digital Medicare card를 사용할 수 있는지 확인하고, 카드의 이름·Individual Reference Number·유효기간이 맞는지 살펴보세요.",
          "Medicare를 myGov에 연결할 때 두 서비스의 이름 정보가 일치해야 합니다. 연결 후 주소, 연락처와 본인 Medicare benefit을 받을 은행 계좌의 account name, BSB, account number를 확인하세요. 같은 가족 카드에 있어도 각 사람의 환급 은행정보는 별도로 관리될 수 있습니다.",
        ],
        checklist: [
          "myGov에 Medicare 서비스 연결",
          "digital card의 이름·번호·유효기간 확인",
          "우편 수령 주소 확인",
          "본인 Medicare 환급용 은행정보 등록",
          "blue interim card라면 expiry와 재확인 조건 기록",
        ],
        sourceIds: ["medicare-source-card", "medicare-source-benefits"],
      },
      {
        id: "use-and-maintain",
        title: "5. 진료 전 비용을 묻고 변경사항을 관리하세요",
        body: [
          "예약할 때 해당 서비스가 Medicare 대상인지, provider가 bulk bill하는지, 먼저 전액을 결제해야 하는지와 예상 out-of-pocket 또는 gap 비용을 확인하세요. bulk billing이면 provider가 Medicare benefit을 전액 지급으로 받아 해당 서비스의 본인부담금이 없지만, 모든 provider와 서비스가 bulk billing되는 것은 아닙니다.",
          "직접 결제한 뒤 Medicare benefit을 받을 수 있는 서비스라면 provider가 전자청구를 처리하거나 본인이 Medicare online account 등을 통해 청구할 수 있습니다. 환급 영수증을 확인하고 주소·이름·가족관계·비자 상태·은행정보가 바뀌거나 카드가 분실되면 공식 온라인 계정이나 Medicare program을 통해 업데이트하세요.",
        ],
        sourceIds: ["medicare-source-card", "medicare-source-benefits"],
      },
    ],
    faqs: [
      {
        id: "medicare-korean-citizen",
        question: "한국인이면 호주 도착 후 Medicare에 바로 가입할 수 있나요?",
        answer:
          "아닙니다. 한국 국적만으로 자동 가입되지는 않습니다. 시민권·영주권, 자격이 되는 영주권 신청 상태 또는 다른 특별 자격이 있는지 확인해야 하며, 한국은 현재 RHCA 국가 목록에 없습니다.",
        sourceIds: ["medicare-source-enrolment", "medicare-source-rhca"],
      },
      {
        id: "medicare-pr-applicant",
        question: "영주권을 신청하면 즉시 Medicare에 가입할 수 있나요?",
        answer:
          "일부 permanent residency 또는 protection visa 신청자는 가입할 수 있지만 모두 자동 대상은 아닙니다. 호주 거주, 신청한 비자 종류, 현재 비자의 근무 권한 또는 자격이 되는 가족관계 조건을 확인하세요.",
        sourceIds: [
          "medicare-source-permanent-resident",
          "medicare-source-documents",
        ],
      },
      {
        id: "medicare-student-visa",
        question: "학생비자와 OSHC가 있으면 Medicare도 받을 수 있나요?",
        answer:
          "일반적인 한국 학생비자와 OSHC만으로 Medicare 가입 자격이 생기는 것은 아닙니다. 현재 비자와 별도 영주권 신청 상태 등 다른 자격이 있는지 확인하고, 자격이 없다면 OSHC 보장과 본인부담 조건을 확인하세요.",
        sourceIds: ["medicare-source-enrolment", "medicare-source-rhca"],
      },
      {
        id: "medicare-online-failed",
        question: "myGov에서 온라인 가입이 되지 않습니다.",
        answer:
          "온라인 신청 대상이 아니거나 신원·비자 정보가 일치하지 않을 수 있습니다. Medicare enrolment form과 지원서류를 이메일 또는 우편으로 제출하는 경로를 확인하고, 자격이 불분명하면 Medicare program에 문의하세요.",
        sourceIds: [
          "medicare-source-enrolment",
          "medicare-source-permanent-resident",
        ],
      },
      {
        id: "medicare-family-over-15",
        question: "가족을 한 번에 같이 신청할 수 있나요?",
        answer:
          "가능할 수 있습니다. 온라인 가족 신청에서 15세 미만 자녀의 신분자료를 제출하고, 15세 이상 가족은 신청번호를 받아 자신의 myGov 계정에서 정보와 신분서류를 완료해야 할 수 있습니다.",
        sourceIds: ["medicare-source-enrolment"],
      },
      {
        id: "medicare-card-waiting",
        question: "플라스틱 카드가 오기 전에는 Medicare를 사용할 수 없나요?",
        answer:
          "가입이 완료되고 myGov에 Medicare가 연결되면 myGov 앱에서 digital Medicare card를 사용할 수 있습니다. 실제 등록 완료 여부와 카드 정보를 먼저 확인하세요.",
        sourceIds: ["medicare-source-card"],
      },
      {
        id: "medicare-all-free",
        question: "Medicare 카드가 있으면 병원과 GP가 모두 무료인가요?",
        answer:
          "아닙니다. bulk billing되는 대상 서비스는 본인부담금이 없지만, provider가 bulk bill하지 않거나 Medicare benefit보다 높은 비용을 청구하면 gap을 부담할 수 있습니다. 예약 전에 예상 비용을 확인하세요.",
        sourceIds: ["medicare-source-benefits"],
      },
      {
        id: "medicare-refund-bank",
        question: "Medicare 환급금이 들어오지 않습니다.",
        answer:
          "청구 상태와 본인 Medicare online account의 은행정보를 확인하세요. account name, BSB와 account number가 정확한지 확인하고 다른 가족 구성원의 계좌와 혼동하지 않았는지 살펴본 뒤 Medicare program에 문의하세요.",
        sourceIds: ["medicare-source-benefits"],
      },
      {
        id: "medicare-blue-card",
        question: "파란색 interim Medicare card를 받았습니다.",
        answer:
          "영주권 신청자 등에게 유효기간이 있는 blue interim card가 발급될 수 있습니다. 카드 expiry와 현재 비자·신청 상태를 확인하고 새 카드가 오지 않으면 만료 전에 Medicare에 문의하세요.",
        sourceIds: ["medicare-source-card"],
      },
    ],
    sourceIds: [
      "medicare-source-enrolment",
      "medicare-source-permanent-resident",
      "medicare-source-documents",
      "medicare-source-card",
      "medicare-source-benefits",
      "medicare-source-rhca",
    ],
    additionalChecks: [
      "현재 비자, 영주권 신청 종류와 가족관계에 따른 개인별 Medicare 가입 자격을 확인하세요.",
      "한국의 RHCA 포함 여부와 임시거주자 관련 Ministerial Order 대상은 신청 직전에 최신 목록을 확인하세요.",
      "Medicare 승인 전후로 민간보험·OSHC·OVHC가 중단되지 않도록 보험 시작일과 종료일을 확인하세요.",
      "진료 예약 전에 Medicare 대상 서비스, bulk billing, 예상 gap과 청구 방식을 provider에게 확인하세요.",
      "긴급하거나 생명을 위협하는 증상이 있으면 가입·카드 문제 해결을 기다리지 말고 즉시 응급의료를 이용하세요.",
    ],
    disclaimer:
      "이 안내는 Medicare 가입과 이용에 관한 일반 생활정보이며 개인의 의료·이민·보험 자문이 아닙니다. 비자, 영주권 신청, 가족관계와 진료 서비스에 따라 자격·보장·비용이 달라질 수 있으므로 Services Australia, Home Affairs, 의료기관 또는 보험사에 개별 확인하세요. 응급 상황에서는 즉시 000 또는 가까운 응급의료기관을 이용하세요.",
  },
  {
    topicId: "mygov-account",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "호주 전역",
    summary:
      "myGov는 여러 호주 정부 서비스를 한곳에서 이용하는 계정입니다. 처음에는 공식 myGov 웹사이트에서 이메일 또는 Digital ID로 계정을 만들고, 이미 계정이 있다면 중복 생성보다 로그인 복구를 먼저 시도하세요. 로그인 문제와 ATO·Medicare 같은 연결 서비스 문제를 구분하고, 메시지로 받은 링크가 아닌 my.gov.au에서 직접 시작하는 것이 안전합니다.",
    keyPoints: [
      "myGov 계정은 공식 웹사이트에서 이메일 주소 또는 Digital ID를 이용해 만들 수 있습니다.",
      "계정 생성 후 Centrelink, Medicare, ATO 등 필요한 정부 서비스를 개별적으로 연결합니다.",
      "username·password·SMS code 문제는 공식 sign in help의 복구 순서대로 확인하세요.",
      "linking code는 모든 서비스에 공통으로 쓰는 번호가 아니며 Centrelink·Child Support·Medicare 연결에 사용하는 일회성 코드입니다.",
      "myGov helpdesk는 계정 로그인과 사용을 지원하지만, 지급·세금·의료 기록 같은 서비스 내용은 해당 기관에 문의해야 합니다.",
      "passkey나 Digital ID 등 강한 로그인 방식과 security review를 사용하고 로그인 정보·코드를 누구에게도 공유하지 마세요.",
    ],
    sections: [
      {
        id: "check-existing-account",
        title: "1. 새 계정을 만들기 전에 기존 계정부터 확인하세요",
        body: [
          "예전에 myGov를 사용했다면 먼저 공식 로그인 화면의 Forgot username 또는 Forgot password를 이용하세요. 등록한 이메일 주소로 username을 확인할 수 있고, 설정한 로그인 정보에 따라 이메일이나 휴대전화로 코드를 받아 password를 변경할 수 있습니다.",
          "SMS code, Code Generator, secret question 또는 passkey 때문에 로그인하지 못하는 경우에도 공식 sign in help에 대체 로그인과 계정 복구 경로가 안내됩니다. 복구가 되지 않아 같은 이메일로 새 계정을 만들어야 한다면 임의로 반복 가입하지 말고 myGov helpdesk에 문의하세요.",
        ],
        checklist: [
          "기억나는 myGov username·등록 이메일",
          "기존 등록 휴대전화 접근 가능 여부",
          "Digital ID·passkey·myGov app 설정 여부",
          "화면에 표시된 오류 문구와 발생 시각",
        ],
        sourceIds: ["mygov-account-source-sign-in-help", "mygov-account-source-contact"],
      },
      {
        id: "create-account",
        title: "2. 처음이라면 공식 웹사이트에서 계정을 만드세요",
        body: [
          "my.gov.au의 Create account에서 이메일 또는 Digital ID 방식을 선택할 수 있습니다. 이메일 방식에서는 이메일로 받은 code를 확인하고, 휴대전화 번호를 선택적으로 추가한 뒤 password와 세 가지 secret question을 설정합니다. 한 이메일 주소는 두 myGov 계정에 함께 사용할 수 없습니다.",
          "계정 생성은 myGov 앱이 아니라 웹사이트에서 시작합니다. 공용 기기를 사용했다면 작업을 마친 뒤 반드시 sign out하고, username과 복구 정보를 타인에게 보이지 않는 안전한 곳에 보관하세요.",
        ],
        checklist: [
          "본인만 접근하는 고유 이메일 주소",
          "이메일 code를 확인할 수 있는 기기",
          "선택 사항인 SMS 수신 가능 휴대전화",
          "다른 서비스에서 재사용하지 않는 password",
          "본인만 기억할 수 있는 secret questions와 answers",
        ],
        sourceIds: ["mygov-account-source-create", "mygov-account-source-security"],
      },
      {
        id: "link-services",
        title: "3. 필요한 정부 서비스를 하나씩 연결하세요",
        body: [
          "계정을 만들었다고 ATO, Medicare 또는 Centrelink가 자동으로 연결되는 것은 아닙니다. myGov의 View and link services에서 필요한 서비스를 선택하고 그 서비스가 요구하는 신원·기록 질문을 완료하세요. 질문과 준비자료는 서비스마다 다릅니다.",
          "Centrelink, Child Support 또는 Medicare 연결에서 일반 질문으로 확인할 수 없다면 해당 기관이 발급한 일회성 linking code를 사용할 수 있습니다. ATO 등 다른 서비스의 연결 문제에는 이 linking code 안내를 그대로 적용하지 말고, myGov의 해당 서비스별 공식 연결 도움말을 확인하세요.",
        ],
        checklist: [
          "연결할 서비스의 고객번호·카드·기존 기록",
          "myGov와 해당 서비스의 이름·생년월일 표기",
          "최근 거래·지급·고용·세금 등 서비스가 요구하는 확인정보",
          "linking code가 필요한 경우 해당 서비스의 공식 발급 경로",
        ],
        sourceIds: ["mygov-account-source-link-services", "mygov-account-source-linking-code"],
      },
      {
        id: "recover-and-escalate",
        title: "4. 로그인 문제와 서비스 문제를 나눠 해결하세요",
        body: [
          "myGov 홈 자체에 로그인할 수 없다면 username, password, code, 잠금 또는 passkey 문제를 sign in help 순서로 확인합니다. 계정에는 들어가지만 특정 서비스만 연결되지 않거나 기록이 보이지 않는다면 그 서비스의 공식 지원 경로로 문의해야 합니다.",
          "myGov helpdesk는 로그인과 계정 사용을 도울 수 있지만 Centrelink payment, Medicare claim, ATO tax record 같은 기관별 결정을 처리하지 않습니다. 문의 전에 오류 화면, 시각, 시도한 경로를 기록하되 password, secret answer, SMS code 또는 passkey를 메모나 상담 메시지에 넣지 마세요.",
        ],
        sourceIds: ["mygov-account-source-sign-in-help", "mygov-account-source-contact"],
      },
      {
        id: "secure-account",
        title: "5. 로그인 수단과 계정 기록을 안전하게 관리하세요",
        body: [
          "myGov는 Digital ID, passkey, authenticator, SMS code 등 여러 MFA 방식을 지원합니다. 본인의 기기와 사용 환경에 맞는 강한 로그인 방식을 설정하고, 계정의 security review와 account history를 정기적으로 확인하세요.",
          "새 기기 로그인이나 설정 변경 알림이 본인의 행동이 아니라면 공식 myGov에 직접 접속해 계정 기록과 보안 설정을 확인하세요. myGov passkey, password, PIN 또는 인증 code는 가족·고용주·대행업체를 포함해 누구와도 공유하지 마세요.",
        ],
        sourceIds: ["mygov-account-source-security", "mygov-account-source-sign-in-help"],
      },
    ],
    faqs: [
      {
        id: "mygov-create-in-app",
        question: "myGov 앱에서 새 계정을 만들 수 있나요?",
        answer: "계정 생성은 공식 myGov 웹사이트에서 먼저 진행합니다. 계정을 만든 뒤 같은 계정으로 myGov 앱을 설정할 수 있습니다.",
        sourceIds: ["mygov-account-source-create"],
      },
      {
        id: "mygov-shared-email",
        question: "가족과 같은 이메일 주소로 각각 계정을 만들 수 있나요?",
        answer: "같은 이메일 주소를 두 myGov 계정에 사용할 수 없습니다. 각 계정에는 고유한 이메일 주소가 필요합니다.",
        sourceIds: ["mygov-account-source-create"],
      },
      {
        id: "mygov-changed-phone",
        question: "휴대전화 번호가 바뀌어 SMS code를 받지 못합니다.",
        answer: "Digital ID, myGov app 또는 passkey처럼 이미 설정한 다른 로그인 방식이 있는지 확인하세요. 사용할 수 없다면 공식 sign in help의 복구 절차를 따르고, 계속 접근할 수 없을 때 myGov helpdesk에 문의하세요.",
        sourceIds: ["mygov-account-source-sign-in-help", "mygov-account-source-contact"],
      },
      {
        id: "mygov-forgot-username",
        question: "myGov username을 잊었습니다.",
        answer: "공식 로그인 화면에서 Forgot username을 선택하고 등록한 이메일 주소를 입력하면 해당 주소로 username을 받을 수 있습니다.",
        sourceIds: ["mygov-account-source-sign-in-help"],
      },
      {
        id: "mygov-linking-code-all-services",
        question: "linking code 하나로 ATO와 Medicare를 모두 연결할 수 있나요?",
        answer: "아닙니다. 공식 안내의 linking code는 Centrelink, Child Support, Medicare를 연결하기 위한 일회성 코드입니다. ATO 연결은 ATO용 공식 연결 절차를 확인하세요.",
        sourceIds: ["mygov-account-source-linking-code", "mygov-account-source-link-services"],
      },
      {
        id: "mygov-service-not-linking",
        question: "myGov에는 로그인되지만 정부 서비스가 연결되지 않습니다.",
        answer: "해당 서비스의 연결 도움말에서 요구하는 기록과 이름 정보를 확인하세요. myGov 계정 자체 문제가 아니라 기관 기록·신원 질문 문제라면 해당 서비스에 문의해야 합니다.",
        sourceIds: ["mygov-account-source-link-services", "mygov-account-source-contact"],
      },
      {
        id: "mygov-locked",
        question: "비밀번호를 여러 번 틀려 계정이 잠겼습니다.",
        answer: "공식 sign in help의 Unlock your account 절차를 따르세요. 잠금 상태와 설정에 따라 security code, secret question과 새 password가 필요할 수 있습니다.",
        sourceIds: ["mygov-account-source-sign-in-help"],
      },
      {
        id: "mygov-suspicious-message",
        question: "myGov라고 적힌 문자나 이메일 링크를 눌러도 되나요?",
        answer: "메시지 링크에서 로그인하지 말고 주소창에 my.gov.au를 직접 입력해 계정 알림과 history를 확인하세요. 로그인 정보나 인증 code는 누구에게도 공유하지 마세요.",
        sourceIds: ["mygov-account-source-security"],
      },
    ],
    sourceIds: [
      "mygov-account-source-create",
      "mygov-account-source-sign-in-help",
      "mygov-account-source-link-services",
      "mygov-account-source-linking-code",
      "mygov-account-source-security",
      "mygov-account-source-contact",
    ],
    additionalChecks: [
      "계정 생성·복구 직전 myGov 공식 화면에서 현재 제공되는 로그인 방식과 요구사항을 확인하세요.",
      "서비스 연결 질문과 준비자료는 ATO, Medicare, Centrelink 등 해당 기관의 최신 안내에서 다시 확인하세요.",
      "전화 지원 번호와 운영시간은 연락 직전 공식 contact 페이지에서 확인하세요.",
      "계정 탈취가 의심되면 메시지의 연락처를 사용하지 말고 my.gov.au에서 직접 보안·지원 경로를 여세요.",
    ],
    disclaimer:
      "이 안내는 myGov 계정 생성·로그인·서비스 연결에 관한 일반 생활정보입니다. 특정 지급, 세금, 의료, 복지 또는 서비스 자격을 판단하지 않으며, 계정 연결이 해당 혜택의 자격이나 승인을 의미하지 않습니다. 계정 문제는 myGov에, 기관별 기록·결정·자격 문제는 해당 정부 서비스에 확인하세요.",
  },
  {
    topicId: "australian-bank-account",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "호주 전역",
    summary:
      "호주 생활용 은행계좌는 급여 수령, 청구서 납부와 debit card 사용의 기본 수단입니다. 먼저 본인의 사용 방식에 맞춰 월 계좌비, ATM·해외거래·overdraft 비용을 비교하고, 선택한 은행이 요구하는 신원·주소 자료를 공식 안내에서 확인하세요. 계좌 개설 조건은 은행과 신청 방식에 따라 다르므로 특정 서류 하나가 모든 은행에서 통한다고 가정하지 않는 것이 중요합니다.",
    keyPoints: [
      "transaction account는 일상 지출과 급여 수령에 쓰며 보통 debit card가 연결됩니다.",
      "은행은 계좌 제공 전에 고객의 신원을 확인해야 하므로 여권 등 허용 서류와 이름 표기를 확인합니다.",
      "월 계좌비뿐 아니라 ATM, 해외거래, 지점 이용, overdraft 비용과 기능을 함께 비교하세요.",
      "예금보호는 모든 금융 앱에 자동 적용되지 않으므로 APRA 승인 ADI와 보호 대상 상품인지 확인하세요.",
      "전자결제 보호는 제공자의 ePayments Code 가입 여부와 계좌 약관을 확인해야 합니다.",
      "모르는 거래나 착오송금을 발견하면 즉시 은행에 신고하고 reference number를 보관하세요.",
      "은행을 사칭한 연락은 끊고 카드 뒷면이나 공식 웹사이트의 번호로 직접 다시 연락하세요.",
    ],
    sections: [
      {
        id: "choose-account",
        title: "1. 계좌 용도와 실제 비용부터 비교하세요",
        body: [
          "급여 수령과 일상 결제가 목적이라면 transaction account와 연결 debit card를 먼저 살펴보세요. 월 계좌비가 없더라도 타사 ATM, 해외 결제·인출, 지점 거래, 환전 또는 overdraft에 별도 비용이 생길 수 있으므로 상품 수수료표와 조건을 읽어야 합니다.",
          "정부 지급을 받거나 concession card가 있는 사람에게 basic bank account가 제공될 수 있지만 개인 자격은 은행이 판단합니다. 광고나 비교 사이트의 순위만 보지 말고 은행 공식 상품 페이지와 약관에서 수수료 면제 조건, 최소 입금, card·digital wallet 기능을 비교하세요.",
        ],
        checklist: [
          "급여·청구서·일상 결제 등 주된 용도",
          "월 계좌비와 면제 조건",
          "ATM·해외거래·지점·overdraft 비용",
          "debit card와 digital wallet 지원",
          "온라인 전용인지 지점 지원이 필요한지",
        ],
        sourceIds: ["australian-bank-account-source-transaction-account"],
      },
      {
        id: "prepare-identification",
        title: "2. 은행이 인정하는 신원·주소 자료를 준비하세요",
        body: [
          "호주 금융기관은 서비스를 제공하기 전에 고객이 누구인지 확인하는 절차를 수행해야 합니다. 이 때문에 계좌 신청에서 이름, 생년월일, 주소와 신분서류를 요구하고, 위험이나 기록 불일치가 있으면 추가 정보를 요청할 수 있습니다.",
          "필요한 서류 목록과 온라인 확인 방식은 은행마다 다릅니다. 신청 전에 해당 은행의 공식 페이지에서 해외 여권, 비자 정보, 호주 주소 증빙 등 인정 자료를 확인하고 모든 서류의 영문 이름·생년월일이 일치하는지 대조하세요. 주소나 현지 서류가 부족하면 임의 정보를 넣지 말고 은행에 가능한 대체 절차를 문의하세요.",
        ],
        checklist: [
          "은행이 인정한다고 명시한 사진 신분서류",
          "현재 비자·체류 정보가 필요한지 여부",
          "호주 거주주소와 인정 가능한 주소 증빙",
          "서류마다 영문 이름·생년월일이 일치하는지",
          "호주 전화번호·이메일과 세금 거주지 질문 준비",
        ],
        sourceIds: ["australian-bank-account-source-identification"],
      },
      {
        id: "verify-provider-protection",
        title: "3. 금융기관과 예금보호 범위를 확인하세요",
        body: [
          "Financial Claims Scheme는 호주에 편입되고 APRA가 승인한 ADI의 호주달러 보호 대상 예금을 계좌 보유자 한 명당 ADI 한 곳에서 합산 25만 달러까지 보호합니다. transaction·savings·cheque account와 term deposit 등 다양한 계좌가 포함될 수 있지만 외화예금과 모든 금융상품이 자동으로 포함되는 것은 아닙니다.",
          "서로 다른 브랜드가 같은 ADI의 banking licence 아래 운영될 수도 있으므로 브랜드 수만으로 보호 한도가 늘어난다고 판단하지 마세요. APRA의 ADI 목록과 Deposit Checker를 확인하고, 큰 금액이나 복잡한 소유 구조는 상품 PDS와 금융기관 설명을 함께 확인하세요.",
        ],
        sourceIds: ["australian-bank-account-source-deposit-protection"],
      },
      {
        id: "activate-securely",
        title: "4. 계좌와 카드를 공식 채널에서 안전하게 설정하세요",
        body: [
          "계좌 개설 뒤에는 은행 공식 앱이나 주소를 직접 입력한 웹사이트에서 card activation, PIN, 알림과 가능한 다중 인증을 설정하세요. 문자·이메일 링크에서 인터넷뱅킹에 로그인하거나 전화 상대가 알려준 ‘안전한 계좌’로 돈을 옮기지 마세요.",
          "은행은 online banking password나 인증 code를 알려 달라고 하거나, 원격접속 소프트웨어 설치와 돈 이체를 요구하지 않는다고 Moneysmart는 안내합니다. 의심스러운 연락은 끊고 카드 뒷면 또는 은행 공식 웹사이트의 번호로 다시 확인하세요.",
        ],
        checklist: [
          "공식 앱·웹사이트에서 card와 PIN 설정",
          "거래·낮은 잔액·새 기기 로그인 알림",
          "고유한 password 또는 passphrase와 가능한 MFA",
          "급여 입금 전 BSB와 account number 재확인",
          "정기결제 전 충분한 잔액과 overdraft 설정 확인",
        ],
        sourceIds: [
          "australian-bank-account-source-security",
          "australian-bank-account-source-transaction-account",
        ],
      },
      {
        id: "resolve-problems",
        title: "5. 모르는 거래·착오송금·민원을 빠르게 기록하세요",
        body: [
          "모르는 출금이나 잘못 보낸 이체를 발견하면 즉시 은행에 신고하고 card·계정을 보호할 조치를 요청하세요. 신고 시각, 거래내역, 은행의 reference number와 답변을 보관하세요. 환급 가능성은 거래 유형, 신고 시점, 제공자의 ePayments Code 가입과 조사 결과에 따라 달라지므로 자동 환급을 단정할 수 없습니다.",
          "은행의 내부 분쟁 해결 절차에 정식 민원을 제기한 뒤 해결되지 않거나 답변에 동의하지 않으면 AFCA 관할과 기한을 확인해 무료 외부 분쟁 해결을 신청할 수 있습니다. 원하는 해결 결과와 관련 statement, 연락 기록, reference number를 준비하세요.",
        ],
        sourceIds: [
          "australian-bank-account-source-transaction-problems",
          "australian-bank-account-source-epayments-code",
          "australian-bank-account-source-complaints",
        ],
      },
    ],
    faqs: [
      {
        id: "bank-account-documents",
        question: "호주 은행계좌를 만들 때 어떤 서류가 꼭 필요한가요?",
        answer: "은행은 신원을 확인해야 하지만 인정 서류와 신청 방식은 금융기관마다 다릅니다. 선택한 은행의 공식 계좌 개설 안내에서 허용 신분·주소 자료를 확인하고 이름과 생년월일 표기를 맞추세요.",
        sourceIds: ["australian-bank-account-source-identification"],
      },
      {
        id: "bank-account-no-address",
        question: "아직 호주 주소 증명이 없으면 계좌를 열 수 없나요?",
        answer: "일률적으로 판단할 수 없습니다. 주소 확인 방식과 대체 자료는 은행마다 다르므로 임의 주소를 입력하지 말고 해당 은행의 공식 안내나 지원팀에 가능한 절차를 확인하세요.",
        sourceIds: ["australian-bank-account-source-identification"],
      },
      {
        id: "bank-account-fees",
        question: "월 계좌비가 0달러면 완전히 무료인가요?",
        answer: "그렇다고 단정할 수 없습니다. ATM, 해외거래, 지점 이용, overdraft 등 다른 비용이 있을 수 있으므로 전체 수수료표와 면제 조건을 확인하세요.",
        sourceIds: ["australian-bank-account-source-transaction-account"],
      },
      {
        id: "bank-account-debit-credit",
        question: "debit card와 credit card는 같은가요?",
        answer: "아닙니다. debit card는 일반적으로 계좌에 있는 돈을 사용하고, credit card는 신용을 빌려 쓰므로 이자·상환·한도가 별도로 적용됩니다. 카드에 credit 기능이 있는지는 상품 조건을 확인하세요.",
        sourceIds: ["australian-bank-account-source-transaction-account"],
      },
      {
        id: "bank-account-deposit-guarantee",
        question: "은행 앱에 넣은 돈은 모두 정부가 보호하나요?",
        answer: "아닙니다. FCS는 호주 편입 APRA 승인 ADI의 보호 대상 호주달러 예금에 적용됩니다. 한도는 계좌 보유자별·ADI별 합산 25만 달러이며, APRA 목록과 상품 정보를 확인해야 합니다.",
        sourceIds: ["australian-bank-account-source-deposit-protection"],
      },
      {
        id: "bank-account-unknown-transaction",
        question: "모르는 거래가 보이면 먼저 무엇을 해야 하나요?",
        answer: "즉시 은행의 공식 fraud·dispute 채널에 신고해 추가 거래를 막을 조치를 요청하고 reference number를 받으세요. PIN, password나 인증 code가 노출됐을 가능성도 함께 알리세요.",
        sourceIds: [
          "australian-bank-account-source-transaction-problems",
          "australian-bank-account-source-security",
        ],
      },
      {
        id: "bank-account-mistaken-transfer",
        question: "잘못 송금하면 자동으로 돌려받을 수 있나요?",
        answer: "자동 환급을 보장할 수 없습니다. 가능한 한 빨리 은행에 신고해야 하며, 처리 규칙과 결과는 신고 시점, 상대 계좌 잔액, ePayments Code 적용과 조사에 따라 달라집니다.",
        sourceIds: [
          "australian-bank-account-source-transaction-problems",
          "australian-bank-account-source-epayments-code",
        ],
      },
      {
        id: "bank-account-complaint",
        question: "은행이 문제를 해결하지 않으면 어디에 민원하나요?",
        answer: "먼저 은행의 내부 분쟁 해결 절차에 정식 민원을 제기하세요. 해결되지 않거나 답변에 동의하지 않으면 AFCA의 관할과 기한을 확인해 무료 외부 분쟁 해결을 신청할 수 있습니다.",
        sourceIds: [
          "australian-bank-account-source-transaction-problems",
          "australian-bank-account-source-complaints",
        ],
      },
      {
        id: "bank-account-safe-account-call",
        question: "은행 직원이 안전한 계좌로 돈을 옮기라고 전화했습니다.",
        answer: "전화를 끊고 이체하지 마세요. 발신번호도 위조될 수 있으므로 카드 뒷면이나 은행 공식 웹사이트의 번호로 직접 다시 연락해 확인하세요.",
        sourceIds: ["australian-bank-account-source-security"],
      },
    ],
    sourceIds: [
      "australian-bank-account-source-transaction-account",
      "australian-bank-account-source-identification",
      "australian-bank-account-source-deposit-protection",
      "australian-bank-account-source-epayments-code",
      "australian-bank-account-source-transaction-problems",
      "australian-bank-account-source-security",
      "australian-bank-account-source-complaints",
    ],
    additionalChecks: [
      "계좌 신청 직전 선택한 은행의 허용 신분·주소 자료와 최신 수수료표를 확인하세요.",
      "예금보호가 중요하면 APRA ADI 목록·Deposit Checker와 해당 상품 PDS를 확인하세요.",
      "전자결제 보호는 ASIC subscriber 목록 또는 계좌 약관에서 제공자의 ePayments Code 가입 여부를 확인하세요.",
      "모르는 거래·착오송금·사기는 지체하지 말고 은행 공식 채널에 신고하고 기록을 보관하세요.",
      "AFCA 민원 전 현재 관할, 접수 기한과 필요한 증빙을 공식 안내에서 확인하세요.",
    ],
    disclaimer:
      "이 안내는 호주 개인 은행계좌 선택·개설·보안과 기본 분쟁 절차에 관한 일반 생활정보이며 개인 금융·법률 조언이 아닙니다. 계좌 승인, 허용 서류, 수수료, 예금보호와 거래 환급 여부는 금융기관·상품·거래 및 개인 상황에 따라 달라집니다. 신청과 큰 금액 예치 전 은행의 최신 약관·PDS와 APRA·ASIC 공식 안내를 확인하세요.",
  },
  {
    topicId: "identity-documents",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "호주 전역·기관별 기준",
    summary:
      "호주에서 신분이나 주소를 증명할 때 모든 신청에 공통으로 적용되는 하나의 서류 조합은 없습니다. 은행, 정부기관, 고용주와 부동산 업체가 각각 필요한 신뢰 수준과 인정 서류를 정하므로, 먼저 수신 기관의 공식 목록·유효기간·사본 인증·번역 조건을 확인하세요. 서류가 부족하거나 이름이 다르면 임의로 수정하지 말고 대체 확인과 기록 정정 절차를 문의하는 것이 안전합니다.",
    keyPoints: [
      "‘100 point ID’라는 표현만 믿지 말고 실제 신청 기관의 현재 인정 서류 목록을 확인하세요.",
      "문서의 영문 이름, 생년월일, document number와 유효기간을 제출 전에 대조하세요.",
      "주소 증명은 발급일·주소·명의와 허용 문서 종류가 기관마다 다릅니다.",
      "표준 신분서류가 없다고 거짓 정보를 넣지 말고 대체 확인 또는 수동 검토 가능성을 문의하세요.",
      "한국어 문서 번역은 수신 기관이 요구하는 NAATI 자격·언어 방향·stamp 조건을 먼저 확인하세요.",
      "Digital ID는 자발적인 온라인 확인 방법이며 여권·면허 같은 원본 신분서류 자체를 없애거나 대체하지 않습니다.",
      "DVS 실패는 곧 가짜 문서라는 뜻이 아니므로 입력값과 발급기관 원본 기록을 확인하세요.",
    ],
    sections: [
      {
        id: "start-with-receiver",
        title: "1. 제출처의 공식 요구사항부터 저장하세요",
        body: [
          "같은 여권이나 청구서라도 은행 계좌, 임대 신청, 취업 확인, 운전면허와 정부 서비스에서 인정 방식이 다를 수 있습니다. 제출처 공식 페이지에서 어떤 문서가 몇 개 필요한지, 원본·공증 사본·전자 확인 중 무엇을 받는지, 문서 유효기간과 발급일 제한이 있는지 확인하세요.",
          "‘100 point check’라는 일반 표현은 기관의 실제 목록을 대신하지 않습니다. 신청 이름, 필요한 서비스, 확인 날짜와 공식 안내 URL을 메모하거나 PDF로 저장하면 창구나 온라인 화면의 요구가 달라졌을 때 확인하기 쉽습니다.",
        ],
        checklist: [
          "신청 기관과 정확한 서비스명",
          "인정 문서 종류·개수 또는 점수표",
          "원본·certified copy·온라인 verification 조건",
          "문서 유효기간과 주소 증명의 발급일 제한",
          "번역·이름 변경·대체 서류 안내",
        ],
        sourceIds: ["identity-documents-source-receiver-requirements"],
      },
      {
        id: "match-records",
        title: "2. 이름·생년월일·주소와 문서 상태를 대조하세요",
        body: [
          "여권, visa record, 운전면허, Medicare card, 출생·혼인·개명 증서에 표시된 영문 이름과 생년월일을 신청서 입력값과 한 글자씩 비교하세요. middle name, 띄어쓰기, 결혼 전 이름처럼 설명 가능한 차이가 있다면 이를 연결하는 문서를 제출처가 인정하는지 확인합니다.",
          "주소 증명은 현재 주소와 본인 명의가 보이는지뿐 아니라 문서 종류와 발급 시점도 확인해야 합니다. 다른 사람 명의의 bill이나 임의로 바꾼 PDF를 제출하지 말고, 새로 도착해 자료가 적다면 기관에 허용되는 대체 문서나 수동 확인을 문의하세요.",
        ],
        checklist: [
          "모든 문서의 영문 이름과 순서",
          "생년월일·document number·expiry date",
          "visa와 passport 연결 상태",
          "현재 주소·명의·발급일이 보이는 자료",
          "이름 변경을 연결하는 공식 증서 필요 여부",
        ],
        sourceIds: [
          "identity-documents-source-dvs",
          "identity-documents-source-receiver-requirements",
        ],
      },
      {
        id: "alternative-verification",
        title: "3. 표준 서류가 부족하면 대체 확인을 요청하세요",
        body: [
          "표준 사진 신분증을 분실했거나 재난, 가족폭력, 수감·출소, 문화적 사정 등으로 일반 서류를 구하기 어려울 수 있습니다. AUSTRAC는 금융기관이 위험을 관리하면서 대체 identification을 사용할 수 있는 유연한 절차를 마련하도록 안내합니다.",
          "이 원칙이 모든 기관에 같은 대체 문서를 의무화하는 것은 아닙니다. 담당자에게 ‘어떤 standard ID가 부족한지’, ‘현재 가진 정부 편지·community record·referee 자료가 무엇인지’를 설명하고 alternative identification, manual review 또는 specialist support team이 있는지 물어보세요. 거절되면 이유와 추가 가능한 자료를 서면으로 요청합니다.",
        ],
        sourceIds: [
          "identity-documents-source-alternative-id",
          "identity-documents-source-receiver-requirements",
        ],
      },
      {
        id: "translate-documents",
        title: "4. 비영문 문서는 번역 조건을 먼저 확인하세요",
        body: [
          "한국어 가족관계·혼인·학력 서류 등이 필요한 경우 직접 번역하거나 AI 번역만 제출하지 말고, 수신 기관이 NAATI-certified translator를 요구하는지 확인하세요. 같은 번역이라도 visa, 법원, 학교, 은행 또는 DFAT legalisation 절차의 원본 첨부와 stamp 조건이 다를 수 있습니다.",
          "NAATI 번역에는 translator의 이름, CPN, 언어 방향과 physical 또는 digital stamp가 표시될 수 있습니다. 비용을 지불하기 전에 필요한 번역 방향, 모든 페이지 번역 여부, 원본 사본 동봉, 전자 stamp 허용 여부를 제출처에 확인하고 NAATI 디렉터리에서 자격을 검증하세요.",
        ],
        sourceIds: ["identity-documents-source-naati"],
      },
      {
        id: "digital-verification",
        title: "5. Digital ID와 온라인 검증 실패를 구분하세요",
        body: [
          "Digital ID는 기존 신분서류 정보를 발급기관 기록과 확인해 온라인에서 본인임을 증명하는 자발적 방법입니다. 서비스마다 필요한 identity proofing level과 지원 provider가 다르며, Digital ID를 만들었다고 물리 신분서류가 폐기되거나 모든 서비스에서 자동 인정되는 것은 아닙니다.",
          "DVS는 이름·생년월일 같은 입력 정보가 문서 발급기관의 원본 기록과 맞는지 비교합니다. 실패하면 반복 제출보다 입력 오타, name order, document number, card version과 발급기관 기록 상태를 확인하세요. 제출처에 오류 code와 수동 확인 경로를 묻고, 필요한 경우 문서 발급기관에서 원본 기록을 정정합니다.",
        ],
        sourceIds: [
          "identity-documents-source-digital-id",
          "identity-documents-source-digital-id-setup",
          "identity-documents-source-dvs",
        ],
      },
    ],
    faqs: [
      {
        id: "identity-universal-100-points",
        question: "호주에서는 항상 100점짜리 신분증 조합이 필요한가요?",
        answer: "아닙니다. 기관과 서비스마다 필요한 확인 수준과 인정 문서가 다릅니다. 인터넷의 일반 목록보다 실제 제출처의 공식 요구사항을 기준으로 준비하세요.",
        sourceIds: ["identity-documents-source-receiver-requirements"],
      },
      {
        id: "identity-no-proof-of-address",
        question: "제 명의의 호주 주소 증명이 아직 없습니다.",
        answer: "다른 사람 명의 문서나 임의 수정본을 제출하지 마세요. 제출 기관에 어떤 대체 주소 자료나 수동 확인을 받는지 문의하고, 현재 가진 공식 우편·계약·정부 기록 중 인정되는 것을 확인하세요.",
        sourceIds: [
          "identity-documents-source-receiver-requirements",
          "identity-documents-source-alternative-id",
        ],
      },
      {
        id: "identity-name-mismatch",
        question: "여권과 다른 문서의 영문 이름이 다릅니다.",
        answer: "임의로 한쪽 이름을 바꾸지 말고 제출처에 어떤 이름 연결 자료를 받는지 확인하세요. 혼인·개명 증서 등 공식 기록이 필요할 수 있으며, 발급기관 기록 자체가 잘못됐다면 먼저 정정해야 합니다.",
        sourceIds: ["identity-documents-source-dvs"],
      },
      {
        id: "identity-foreign-passport",
        question: "한국 여권만으로 모든 신원확인이 가능한가요?",
        answer: "그렇다고 단정할 수 없습니다. 일부 절차는 foreign passport를 받지만 주소, visa 또는 추가 문서를 요구할 수 있습니다. 제출 기관의 현재 문서 조합을 확인하세요.",
        sourceIds: [
          "identity-documents-source-receiver-requirements",
          "identity-documents-source-digital-id-setup",
        ],
      },
      {
        id: "identity-translation",
        question: "한국어 서류를 제가 영어로 번역해도 되나요?",
        answer: "수신 기관의 기준에 따라 다릅니다. 공식 용도에서는 NAATI-certified translator와 stamp를 요구할 수 있으므로 번역 전에 제출처의 조건을 확인하세요.",
        sourceIds: ["identity-documents-source-naati"],
      },
      {
        id: "identity-digital-id-replaces-documents",
        question: "Digital ID가 있으면 실물 신분증은 필요 없나요?",
        answer: "아닙니다. Digital ID는 기존 신분 정보를 온라인에서 확인하는 선택 수단이며 물리 신분서류 자체를 대체하지 않습니다. 이용하려는 서비스가 해당 Digital ID를 지원하는지도 확인해야 합니다.",
        sourceIds: ["identity-documents-source-digital-id"],
      },
      {
        id: "identity-dvs-failed",
        question: "온라인 document verification이 실패하면 가짜 문서로 의심받은 건가요?",
        answer: "반드시 그렇지는 않습니다. 입력 오타, 이름 순서, document number, card version 또는 발급기관 기록 불일치가 원인일 수 있습니다. 오류를 반복하지 말고 제출처와 발급기관에 확인하세요.",
        sourceIds: ["identity-documents-source-dvs"],
      },
      {
        id: "identity-no-standard-id",
        question: "사진 신분증을 모두 잃어버렸습니다.",
        answer: "거짓 정보를 만들지 말고 기관에 alternative identification이나 manual review를 요청하세요. 금융기관은 상황과 위험을 고려해 대체 자료를 사용할 수 있지만 실제 수용 여부는 해당 기관이 판단합니다.",
        sourceIds: ["identity-documents-source-alternative-id"],
      },
      {
        id: "identity-send-copy-email",
        question: "신분증 사진을 이메일로 보내도 안전한가요?",
        answer: "먼저 제출처가 안내한 공식 업로드·Digital ID 등 더 안전한 방법이 있는지 확인하세요. 필요한 정보만 제공하고, 예상하지 못한 이메일이나 문자 링크에는 신분증 사본을 보내지 마세요.",
        sourceIds: [
          "identity-documents-source-digital-id",
          "identity-documents-source-digital-id-setup",
        ],
      },
    ],
    sourceIds: [
      "identity-documents-source-receiver-requirements",
      "identity-documents-source-alternative-id",
      "identity-documents-source-digital-id",
      "identity-documents-source-digital-id-setup",
      "identity-documents-source-dvs",
      "identity-documents-source-naati",
    ],
    additionalChecks: [
      "제출 직전 수신 기관의 공식 문서 목록, 유효기간, 사본 인증과 주소 증명 조건을 확인하세요.",
      "비영문 문서는 번역 주문 전에 NAATI 자격·언어 방향·stamp·원본 첨부 요구를 확인하세요.",
      "Digital ID 사용 전 해당 서비스의 지원 provider와 필요한 identity proofing level을 확인하세요.",
      "온라인 확인 실패 시 오류 code를 보관하고 제출처와 문서 발급기관에서 입력값·원본 기록을 확인하세요.",
      "분실·재난·가족폭력 등으로 표준 서류가 없으면 기관의 alternative identification 또는 specialist support 경로를 요청하세요.",
    ],
    disclaimer:
      "이 안내는 호주에서 신분·주소 자료를 준비하고 확인 오류를 해결하기 위한 일반 생활정보입니다. 특정 기관의 승인, 신원 확인 통과, 법적 이름 또는 이민·금융 자격을 판단하지 않습니다. 인정 서류와 번역·인증·유효기간은 기관과 서비스마다 다르므로 제출 전 해당 기관의 최신 공식 기준을 확인하세요.",
  },
  {
    topicId: "phone-internet",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "호주 전역·주소별 서비스 범위",
    summary:
      "호주에서 휴대전화나 인터넷을 고를 때는 광고 가격만 보지 말고 실제 사용할 주소의 커버리지, Critical Information Summary(CIS), 계약·장비·해지 비용과 예상 속도를 함께 비교하세요. 번호 이동이나 NBN 연결은 기존 서비스를 성급히 해지하지 말고 새 통신사의 공식 절차로 진행하며, 장애·요금 문제는 기록과 complaint reference를 남기는 것이 중요합니다.",
    keyPoints: [
      "가입 전 통신사가 제공하는 CIS에서 전체 비용·포함 서비스·계약기간·해지 조건을 확인하세요.",
      "coverage map은 예측 정보이므로 집 안과 출퇴근 장소의 실제 수신을 보장하지 않습니다.",
      "기존 번호를 유지하려면 번호 이동이 끝나기 전에 기존 서비스를 먼저 해지하지 마세요.",
      "NBN 주소 상태, busy-period speed, modem·설치 비용과 정전 시 작동 여부를 확인하세요.",
      "장애·속도·연결 문제는 보통 가입한 소매 통신사에 먼저 신고하고 reference number를 받으세요.",
      "요금 납부가 어렵다면 연체를 방치하지 말고 통신사에 financial hardship 또는 payment assistance를 요청하세요.",
      "통신사가 문제를 해결하지 못하면 TIO의 무료 외부 분쟁 해결 범위를 확인하세요.",
    ],
    sections: [
      {
        id: "compare-needs-and-coverage",
        title: "1. 사용 장소와 용도부터 정하고 커버리지를 비교하세요",
        body: [
          "휴대전화는 집·직장·통학 경로에서 통화와 data가 필요한지, 해외전화·roaming·hotspot을 쓰는지 정리하세요. prepaid는 먼저 충전한 범위에서 사용하고 postpaid는 청구 주기와 계약 조건이 적용되므로, 이름만으로 더 저렴하다고 판단하지 말고 같은 사용량의 총비용을 비교합니다.",
          "통신사 coverage map에서 정확한 주소와 자주 가는 장소를 확인하되 지도는 모델링된 예측이라는 점을 기억하세요. outdoor, indoor, in-vehicle 표시가 다를 수 있고 건물 재료, 지형, 기기와 네트워크 혼잡으로 실제 품질이 달라질 수 있습니다. 가능하면 계약·환불 조건 안에서 실제 장소의 수신도 점검하세요.",
        ],
        checklist: [
          "집·직장·통학 경로의 coverage map",
          "월 data, 통화, 국제전화와 roaming 필요량",
          "prepaid expiry 또는 postpaid billing cycle",
          "본인 기기의 network·SIM/eSIM 호환성",
          "반품·cooling-off·coverage 불만 처리 조건",
        ],
        sourceIds: ["phone-internet-source-coverage", "phone-internet-source-cis"],
      },
      {
        id: "read-cis-and-contract",
        title: "2. CIS와 계약에서 전체 비용을 확인하세요",
        body: [
          "모든 통신사는 상품의 주요 조건을 요약한 CIS를 제공해야 합니다. 월 기본료뿐 아니라 activation·installation·modem·SIM·초과 사용·국제통화·roaming 비용, 최소 계약기간, early termination과 남은 단말기 상환액을 확인하고 가입 시점의 문서를 저장하세요.",
          "‘unlimited’ 같은 표현에도 속도 제한, fair use, 제외 서비스 또는 특정 상황의 추가 비용이 있을 수 있습니다. 판매 직원의 말과 CIS가 다르면 서면 조건을 요청하고 이해되지 않는 항목을 확인한 뒤 동의하세요. 결합 할인은 한 서비스를 해지할 때 전체 가격이 어떻게 바뀌는지도 봅니다.",
        ],
        checklist: [
          "CIS와 전체 계약 조건 PDF",
          "최소·월 총비용과 일회성 비용",
          "data·속도 제한과 초과 사용 처리",
          "기기 상환·early termination 잔액",
          "자동결제, bill 발행과 complaint 방법",
        ],
        sourceIds: ["phone-internet-source-cis"],
      },
      {
        id: "switch-and-port-safely",
        title: "3. 번호 이동과 통신사 변경을 안전하게 진행하세요",
        body: [
          "현재 계약의 해지 비용과 단말기 잔액을 먼저 확인한 뒤 새 통신사에 번호 이동을 요청하세요. 유지하려는 번호는 이동 시점에 활성 상태여야 하므로 기존 SIM이나 서비스를 먼저 해지하지 않습니다. 새 통신사가 절차를 진행하며 추가 신원 확인을 요구할 수 있습니다.",
          "예상하지 않은 번호 이동 문자나 인증 code가 오면 링크를 누르거나 code를 전달하지 말고 현재 통신사의 공식 앱·웹사이트·청구서 번호로 즉시 확인하세요. 발신자가 통신사를 사칭할 수 있으므로 cold call만으로 계약을 옮기거나 신분·결제 정보를 주지 마세요. 완료 뒤 기존 계정의 최종 bill과 자동결제도 확인합니다.",
        ],
        sourceIds: ["phone-internet-source-porting", "phone-internet-source-cis"],
      },
      {
        id: "connect-nbn",
        title: "4. 주소별 NBN 상태와 설치·장비를 확인하세요",
        body: [
          "nbn 공식 주소 확인에서 연결 가능 여부와 기술을 확인한 다음 소매 통신사의 요금제를 비교하세요. key facts sheet의 busy-period speed, 예상 사용에 맞는 speed tier, modem 호환성, 표준·비표준 설치 비용, technician appointment와 임대인·strata 승인이 필요한지를 주문 전에 묻습니다.",
          "인터넷 전화, medical alarm, security alarm과 lift phone 같은 장비는 NBN이나 정전 때 계속 작동한다고 가정하지 마세요. 장비 제공자와 통신사에 호환성과 backup 방식을 확인하고, 생명·안전에 중요한 경우 이동통신 등 별도 비상 연락수단을 준비합니다. 연결·속도·dropout·누락 장비 문제는 우선 가입한 통신사에 접수하세요.",
        ],
        checklist: [
          "nbn 공식 주소 결과와 연결 기술",
          "busy-period speed와 실제 예상 속도",
          "modem·connection box·배선 위치와 전원",
          "설치 appointment와 추가 내부 배선 비용",
          "전화·alarm·의료 장비 호환성과 정전 backup",
        ],
        sourceIds: [
          "phone-internet-source-nbn-plan",
          "phone-internet-source-nbn-connect",
        ],
      },
      {
        id: "fix-bills-faults-complaints",
        title: "5. 요금·장애 문제를 기록하고 단계적으로 해결하세요",
        body: [
          "모르는 청구나 반복되는 장애가 있으면 날짜·시간·주소·오류 화면·speed test 조건과 영향을 기록하고 통신사에 정식 complaint로 접수해 reference number와 답변 시점을 받으세요. NBN 서비스의 연결, outage, dropout, speed와 missing equipment는 보통 소매 통신사가 첫 문의처입니다.",
          "납부가 어렵다면 통신사의 payment assistance policy를 찾아 financial hardship 또는 payment assistance라고 분명히 요청하세요. 통신사는 상황에 맞는 지원 옵션을 논의하고 연결 유지를 중심으로 도와야 합니다. 통신사와 해결되지 않거나 연락할 수 없거나 답변에 동의하지 않으면 기록을 준비해 TIO에 무료로 민원을 제기할 수 있습니다.",
        ],
        sourceIds: [
          "phone-internet-source-nbn-connect",
          "phone-internet-source-hardship",
          "phone-internet-source-complaints",
        ],
      },
    ],
    faqs: [
      {
        id: "phone-prepaid-postpaid",
        question: "prepaid와 postpaid 중 어느 것이 더 저렴한가요?",
        answer: "항상 한쪽이 더 저렴하지는 않습니다. 같은 data·통화 사용량을 기준으로 충전 유효기간, 월 청구액, 초과 사용, 할인과 계약·기기 비용을 CIS에서 비교하세요.",
        sourceIds: ["phone-internet-source-cis"],
      },
      {
        id: "phone-unlimited",
        question: "unlimited 요금제면 추가 조건이 전혀 없나요?",
        answer: "그렇다고 단정할 수 없습니다. 속도 제한, fair use, roaming·국제전화 제외나 다른 비용이 있을 수 있으므로 CIS와 전체 계약의 포함·제외 항목을 확인하세요.",
        sourceIds: ["phone-internet-source-cis"],
      },
      {
        id: "phone-coverage-guarantee",
        question: "coverage map에 표시되면 집 안에서도 반드시 잘 되나요?",
        answer: "아닙니다. 지도는 예측 정보이며 건물 재료, 지형, 기기와 혼잡에 따라 실제 수신이 달라집니다. indoor 표시를 확인하고 반품·민원 조건 안에서 주 사용 장소의 실제 품질도 점검하세요.",
        sourceIds: ["phone-internet-source-coverage"],
      },
      {
        id: "phone-keep-number",
        question: "통신사를 바꿔도 지금 번호를 유지할 수 있나요?",
        answer: "대부분의 활성 mobile 또는 local 번호는 이동을 요청할 수 있습니다. 새 통신사에 신청하고 본인 확인을 완료해야 하며, 실제 가능 여부와 절차는 번호·서비스 상황에 따라 확인해야 합니다.",
        sourceIds: ["phone-internet-source-porting"],
      },
      {
        id: "phone-cancel-before-port",
        question: "번호 이동 전에 기존 SIM을 해지해야 하나요?",
        answer: "아닙니다. 유지할 번호는 이동할 때 활성 상태여야 하므로 먼저 해지하지 마세요. 새 통신사의 공식 porting 절차를 따르고 완료 뒤 기존 계정의 최종 bill을 확인하세요.",
        sourceIds: ["phone-internet-source-porting"],
      },
      {
        id: "internet-nbn-contact",
        question: "NBN 인터넷이 끊기면 nbn에 바로 연락하나요?",
        answer: "연결, outage, dropout, speed, 누락 장비와 appointment 문제는 보통 가입한 소매 통신사에 먼저 신고합니다. nbn 장비의 재산 손상이나 설치 workmanship 등은 nbn의 안내 범위를 확인하세요.",
        sourceIds: ["phone-internet-source-nbn-connect"],
      },
      {
        id: "internet-power-outage",
        question: "정전 중에도 NBN 전화와 인터넷이 작동하나요?",
        answer: "항상 작동한다고 가정하면 안 됩니다. 연결 기술, 전원과 battery backup에 따라 다르므로 통신사·장비 제공자에게 확인하고 중요한 전화·alarm에는 별도 backup을 준비하세요.",
        sourceIds: [
          "phone-internet-source-nbn-plan",
          "phone-internet-source-nbn-connect",
        ],
      },
      {
        id: "phone-bill-hardship",
        question: "통신요금을 당장 낼 수 없으면 어떻게 하나요?",
        answer: "연체를 방치하지 말고 통신사의 공식 채널에 financial hardship 또는 payment assistance를 요청하세요. 통신사는 payment assistance policy를 제공하고 상황에 맞는 옵션과 연결 유지 방법을 논의해야 합니다.",
        sourceIds: ["phone-internet-source-hardship"],
      },
      {
        id: "phone-complaint-tio",
        question: "통신사가 문제를 해결하지 않으면 어디에 민원하나요?",
        answer: "먼저 통신사에 정식 complaint를 제기하고 reference number를 받으세요. 해결되지 않거나 연락할 수 없거나 답변에 동의하지 않으면 TIO의 무료 외부 분쟁 해결을 이용할 수 있습니다.",
        sourceIds: ["phone-internet-source-complaints"],
      },
      {
        id: "phone-unexpected-port-code",
        question: "신청하지 않은 번호 이동 인증 문자가 왔습니다.",
        answer: "code를 공유하거나 링크를 누르지 말고 현재 통신사의 공식 앱·웹사이트·청구서 연락처로 즉시 확인하세요. 계정 password도 공식 채널에서 점검하고 승인하지 않은 이동임을 알리세요.",
        sourceIds: ["phone-internet-source-porting"],
      },
    ],
    sourceIds: [
      "phone-internet-source-cis",
      "phone-internet-source-coverage",
      "phone-internet-source-porting",
      "phone-internet-source-nbn-plan",
      "phone-internet-source-hardship",
      "phone-internet-source-nbn-connect",
      "phone-internet-source-complaints",
    ],
    additionalChecks: [
      "가입 직전 통신사의 최신 CIS, 전체 계약과 coverage map을 저장하세요.",
      "번호 이동 전 기존 계약·기기 잔액을 확인하고 완료 전 기존 번호를 해지하지 마세요.",
      "NBN 주문 전 주소 결과, busy-period speed, 장비·설치 비용과 정전 대책을 확인하세요.",
      "장애·요금 문제는 날짜와 증거를 기록하고 통신사 complaint reference를 받으세요.",
      "payment assistance와 TIO 접수 조건은 신청 시점의 공식 안내에서 다시 확인하세요.",
    ],
    disclaimer:
      "이 안내는 호주 휴대전화·인터넷 선택, 연결, 요금과 민원에 관한 일반 생활정보입니다. 특정 주소의 coverage·속도·연결 가능성, 계약 승인, 할인·환불 또는 분쟁 결과를 보장하지 않습니다. 가입·해지·번호 이동 전 통신사의 최신 CIS와 계약, nbn 주소 결과 및 ACMA·TIO 공식 안내를 확인하세요.",
  },
  {
    topicId: "home-utilities",
    locale: "ko",
    publicationStatus: "korean_test_publish",
    risk: "R2",
    checkedAt: "2026-07-16",
    jurisdictionLabel: "호주 전역·주별 에너지 및 임대 규정",
    summary:
      "호주에서 이사하며 전기·가스·수도를 연결하거나 해지할 때는 입주·퇴거일, 계정 명의, 계량기 번호와 사진을 먼저 남기세요. 에너지 소매시장과 소비자 보호, 수도요금의 임차인 부담은 주·준주와 공급 지역에 따라 다르므로 전국에 하나의 절차가 있다고 가정하지 말고 주소에 맞는 정부 비교 도구·retailer·distributor·수도사업자와 tenancy authority를 확인해야 합니다.",
    keyPoints: [
      "입주·퇴거일에 계량기 번호와 수치를 주소가 보이도록 촬영하고 원본을 보관하세요.",
      "새 집에 전기가 켜져 있어도 본인 명의의 retailer 계약과 계정 시작일을 확인해야 합니다.",
      "Energy Made Easy는 일부 주·준주에만 적용되므로 주소에 맞는 정부 비교 도구를 사용하세요.",
      "bill의 이름·주소·meter number·청구기간·actual/estimate와 supply·usage charge를 확인하세요.",
      "이전 거주자 사용기간이 포함된 청구는 바로 납부하거나 무시하지 말고 서면으로 검토를 요청하세요.",
      "수도요금 책임은 lease, 별도 계량, 주별 임대법과 local water provider 규칙을 확인하세요.",
      "요금·계약은 retailer, 정전·배관망은 distributor가 주 문의처이며 미해결 분쟁은 지역 ombudsman으로 진행합니다.",
      "납부 곤란이나 단전 경고는 방치하지 말고 즉시 hardship·payment assistance를 요청하세요.",
    ],
    sections: [
      {
        id: "record-move",
        title: "1. 입주·퇴거일과 계량기를 기록하세요",
        body: [
          "입주 전에 임대계약서의 시작일, 정확한 주소와 unit 번호, 전기·가스·수도 계정 책임을 확인하세요. 열쇠를 받은 날에는 meter number와 reading이 함께 보이도록 사진을 찍고 촬영 시각이 남은 원본을 보관합니다. 계량기 위치를 모르거나 locked meter room이라면 property manager 또는 building manager에게 접근 방법을 요청하세요.",
          "기존 집을 떠날 때는 retailer에 충분한 처리 시간을 두고 종료일과 최종 meter read 방식을 확인하세요. share house에서 계정 명의자가 나가면 남는 사람이 자동으로 책임을 승계한다고 가정하지 말고 새 명의·계약 시작일을 정합니다. 새 집에 공급이 켜져 있더라도 사용 전에 본인 계정이 만들어졌는지 확인하세요.",
        ],
        checklist: [
          "lease의 입주·퇴거일과 정확한 unit 주소",
          "전기·가스·수도 meter number와 reading 사진",
          "이전·새 주소의 retailer와 account number",
          "연결·종료 날짜와 confirmation number",
          "share house 명의 변경과 비용 정산 기록",
        ],
        sourceIds: ["home-utilities-source-moving", "home-utilities-source-renters"],
      },
      {
        id: "compare-energy",
        title: "2. 주소에 맞는 정부 도구로 전기·가스 요금제를 비교하세요",
        body: [
          "비교 전에 최근 bill이나 예상 가구원 수, 난방·온수 방식, solar 여부와 주 사용 시간을 준비하세요. 일일 supply charge, kWh 또는 MJ당 usage charge, peak·off-peak·time-of-use, 할인 조건, 계약기간, 결제 수수료와 종료 비용을 연간 예상비용으로 비교합니다. 가장 큰 할인율이 항상 가장 낮은 총비용을 뜻하지는 않습니다.",
          "Energy Made Easy는 ACT, NSW, Queensland, South Australia와 Tasmania의 가정·소규모 사업자용 정부 서비스입니다. Victoria는 Victorian Energy Compare, WA와 NT는 해당 정부의 가격·retailer 안내를 사용합니다. 지역에 retailer 선택권이 없을 수도 있으므로 상업 비교 사이트가 보여주는 목록만으로 전체 시장이라고 판단하지 마세요.",
        ],
        checklist: [
          "현재 주소의 주·준주와 network area",
          "정부 비교 도구의 적용 지역",
          "daily supply와 usage·time-of-use 요금",
          "할인 만료·조건과 계약 종료 비용",
          "solar feed-in, controlled load와 smart meter tariff",
        ],
        sourceIds: ["home-utilities-source-compare", "home-utilities-source-moving"],
      },
      {
        id: "check-bill",
        title: "3. 첫 bill과 이전 거주자 청구를 항목별로 확인하세요",
        body: [
          "첫 bill에서 본인 이름, service address, meter number, account start date와 billing period를 입주 기록과 대조하세요. reading이 actual인지 estimate인지, 시작·종료 reading, 사용량, daily supply charge와 기타 fee도 확인합니다. 사진과 다른 estimate라면 retailer에 사진 제출 또는 meter read 검토 방법을 문의하세요.",
          "입주 전 기간이나 이전 거주자 이름의 금액이 포함됐다고 생각되면 임의로 bill을 수정하거나 무시하지 마세요. retailer에 정식 billing complaint를 제기해 lease 시작일, 계정 confirmation과 meter 사진을 제공하고, disputed amount와 현재 사용분을 구분한 서면 답변 및 reference number를 요청하세요. 책임 여부는 실제 계약·사용기간과 지역 규칙에 따라 판단됩니다.",
        ],
        sourceIds: ["home-utilities-source-bill", "home-utilities-source-complaints"],
      },
      {
        id: "confirm-water-and-rental",
        title: "4. 수도와 임대주택의 책임을 별도로 확인하세요",
        body: [
          "수도는 전기처럼 모든 지역에서 임차인이 retailer를 선택하는 구조가 아닙니다. 임대인, property manager, local council 또는 water provider 중 누가 account holder인지, usage와 fixed service charge를 누가 부담하는지, 별도 meter가 있는지를 lease와 입주 안내에서 확인하세요.",
          "임차인의 water usage 부담, water efficiency, 누수 수리와 청구 방식은 주·준주별 tenancy law에 따라 다릅니다. 아파트·retirement village·caravan park의 embedded network나 bulk meter도 일반 소매계정과 절차가 다를 수 있습니다. 청구서만 보고 책임을 단정하지 말고 property manager와 해당 주 tenancy authority 또는 ombudsman에 근거를 확인하세요.",
        ],
        sourceIds: ["home-utilities-source-renters", "home-utilities-source-complaints"],
      },
      {
        id: "resolve-outage-hardship",
        title: "5. 정전·단전 경고·납부 곤란을 즉시 구분해 대응하세요",
        body: [
          "주변도 정전됐거나 전선·가스 냄새 같은 network·안전 문제가 있으면 bill의 distributor 또는 공식 outage·emergency 번호를 이용하세요. 본인 계정의 연결·계약·청구 문제는 retailer에 접수합니다. 위험한 전선이나 가스 누출이 의심되면 접근하거나 직접 수리하지 말고 지역 emergency 지침을 따르세요.",
          "bill을 낼 수 없거나 disconnection warning을 받으면 기다리지 말고 retailer에 payment difficulty, hardship 또는 payment assistance를 요청하고 감당 가능한 방안을 논의하세요. life support 장비가 있다면 보호가 자동 적용된다고 가정하지 말고 retailer와 distributor의 등록·의료 확인 절차 및 backup plan을 즉시 확인합니다. 제공자와 해결되지 않으면 complaint reference를 갖고 해당 주·준주의 무료 energy 또는 energy and water ombudsman에 문의하세요.",
        ],
        sourceIds: [
          "home-utilities-source-rights",
          "home-utilities-source-bill",
          "home-utilities-source-complaints",
        ],
      },
    ],
    faqs: [
      {
        id: "utilities-already-on",
        question: "입주했는데 전기가 이미 켜져 있으면 그냥 사용해도 되나요?",
        answer: "공급이 켜져 있어도 본인 명의 계약이 있다는 뜻은 아닙니다. retailer에 service address, account start date와 요금제를 확인하고 입주일 meter 사진을 보관하세요.",
        sourceIds: ["home-utilities-source-moving"],
      },
      {
        id: "utilities-previous-occupant-bill",
        question: "이전 세입자 사용분으로 보이는 bill이 왔습니다.",
        answer: "바로 납부하거나 무시하지 말고 retailer에 billing complaint를 제기하세요. lease 시작일, 본인 계정 confirmation과 입주일 meter 사진을 제공하고 청구기간·reading 검토와 서면 답변을 요청하세요.",
        sourceIds: ["home-utilities-source-bill", "home-utilities-source-complaints"],
      },
      {
        id: "utilities-estimated-bill",
        question: "bill에 estimated reading이라고 표시돼 있습니다.",
        answer: "meter number와 실제 reading 사진을 대조한 뒤 retailer에 customer read 제출 또는 bill review 방법을 문의하세요. estimate가 사용된 이유와 다음 actual read 예정도 확인하세요.",
        sourceIds: ["home-utilities-source-bill"],
      },
      {
        id: "utilities-best-comparison-site",
        question: "호주 전역에서 같은 비교 사이트를 사용하나요?",
        answer: "아닙니다. Energy Made Easy의 적용 지역은 ACT·NSW·QLD·SA·TAS입니다. Victoria·WA·NT는 energy.gov.au가 연결하는 각 정부 경로를 사용하고, 주소에 retailer 선택권이 있는지도 확인하세요.",
        sourceIds: ["home-utilities-source-compare"],
      },
      {
        id: "utilities-water-renter",
        question: "임차인은 수도요금을 항상 내야 하나요?",
        answer: "전국 공통으로 단정할 수 없습니다. lease, 별도 계량 여부, charge 종류와 해당 주 tenancy law를 확인하고 property manager 또는 tenancy authority에 근거를 요청하세요.",
        sourceIds: ["home-utilities-source-renters"],
      },
      {
        id: "utilities-outage-contact",
        question: "정전이면 retailer와 distributor 중 어디에 연락하나요?",
        answer: "network outage, 전선·가스관과 공급 품질은 보통 distributor가 담당하고 계정·요금·계약·연결 주문은 retailer가 담당합니다. bill의 공식 연락처와 outage 지도를 확인하세요.",
        sourceIds: ["home-utilities-source-complaints"],
      },
      {
        id: "utilities-cannot-pay",
        question: "에너지 bill을 기한 안에 낼 수 없습니다.",
        answer: "연체나 경고를 방치하지 말고 즉시 retailer에 payment difficulty 또는 hardship 지원을 요청하세요. 가능한 payment plan과 지원 정책을 서면으로 받고 감당할 수 없는 조건이면 지역 ombudsman에 문의하세요.",
        sourceIds: ["home-utilities-source-rights", "home-utilities-source-bill"],
      },
      {
        id: "utilities-life-support",
        question: "가정에서 life support 장비를 사용하면 단전되지 않나요?",
        answer: "자동 보호라고 가정하면 안 됩니다. retailer와 distributor에 life support 등록, 필요한 의료 확인과 현재 보호 범위를 즉시 문의하고 정전에 대비한 장비 제공자의 backup plan도 준비하세요.",
        sourceIds: ["home-utilities-source-rights"],
      },
      {
        id: "utilities-complaint",
        question: "공급사가 청구 문제를 해결하지 않습니다.",
        answer: "공급사에 정식 complaint로 접수해 reference number와 원하는 해결 결과를 남기세요. 해결되지 않으면 AER의 연락처 안내에서 해당 주·준주의 무료 energy 또는 energy and water ombudsman을 확인하세요.",
        sourceIds: ["home-utilities-source-complaints"],
      },
    ],
    sourceIds: [
      "home-utilities-source-compare",
      "home-utilities-source-moving",
      "home-utilities-source-bill",
      "home-utilities-source-rights",
      "home-utilities-source-complaints",
      "home-utilities-source-renters",
    ],
    additionalChecks: [
      "이사 전 retailer의 현재 연결·종료 처리시간과 필요한 meter read 방식을 확인하세요.",
      "비교 전 주소의 주·준주와 정부 에너지 비교 서비스 적용 범위를 확인하세요.",
      "첫 bill의 service address, meter number, account start date, actual/estimate와 charge를 확인하세요.",
      "수도·embedded network·임차인 부담은 lease와 해당 주 tenancy authority에서 확인하세요.",
      "단전 경고·hardship·life support·민원은 현재 관할의 regulator와 ombudsman 안내를 다시 확인하세요.",
    ],
    disclaimer:
      "이 안내는 호주 가정의 전기·가스·수도 연결, 청구와 기본 민원 절차에 관한 일반 생활정보입니다. 특정 요금제가 가장 저렴하다는 판단, 이전 거주자 청구 책임, 임차인의 수도요금 의무, 지원 자격 또는 단전·분쟁 결과를 결정하지 않습니다. 주소의 주·준주, lease, 계량 방식과 공급자에 따라 규칙이 다르므로 계약·지급·민원 전에 해당 정부 regulator, tenancy authority와 provider의 최신 안내를 확인하세요.",
  },
] as const satisfies readonly TopicGuide[];

export function getTopicGuide(
  topicId: string,
  locale: "ko" | "en",
): TopicGuide | undefined {
  if (locale !== "ko") return undefined;
  return TOPIC_GUIDES.find((guide) => guide.topicId === topicId);
}
